import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Button, IconButton, Paper,
  TextField, FormControl, InputLabel, Select, MenuItem, Alert,
  Autocomplete, InputAdornment, Tooltip, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, createFilterOptions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Description as DescriptionIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import {
  getTransfer, createTransfer, updateTransfer, shipTransfer, receiveTransfer,
  reverseShipTransfer, reverseReceiveTransfer,
  getWarehouses, getCalculatedStockLevels, getInventoryBatches, getSkuBatches, getBrandSubsidiaryMappings,
  formatNumber,
} from '../../services/inventoryService';
import { getSuppliers } from '../../services/procurementService';
import brandService from '../../services/brandService';
import { useBrand } from '../../context/BrandContext';
import { useSubsidiary } from '../../context/SubsidiaryContext';
import api from '../../config/api';
import { buildStockLevelParams, catalogToSkuOptions, mergeCatalogWithStock } from './warehouseTransferStockOptions';
import { skippedCostRows, skippedCostRowsWarning } from '../../utils/cogsWriteFeedback';
import ConfirmDialog from '../common/ConfirmDialog';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';

const extractApiError = (e, fallback = 'Request failed') => {
  const d = e?.response?.data;
  if (d?.error) return d.error;
  if (Array.isArray(d?.errors) && d.errors.length) {
    return d.errors.map(x => x.path ? `${x.msg} (${x.path})` : x.msg).join('; ');
  }
  return e?.message || fallback;
};

// SKU picker filter: narrow by typed text against SKU + product name, and cap the
// list so the dropdown stays responsive and actually filters on type.
const skuFilter = createFilterOptions({
  stringify: (o) => `${o.sku || ''} ${o.product_name || ''}`,
  limit: 50,
});

// Warehouse pickers match on code, name, city AND country (Rishabh 2026-07-29: codes like
// "BT" -> "Omnipack PL" aren't memorable, so typing the name/city must find it). getOptionLabel
// stays "name (code)"; this only widens the FILTER key to include city/country too.
const warehouseFilter = createFilterOptions({
  stringify: (w) => `${w?.code || ''} ${w?.name || ''} ${w?.city || ''} ${w?.country || ''}`,
});

const EMPTY_ITEM = {
  sku: '', batch_number: '', quantity: '', quantity_shipped: '', quantity_received: '',
  hs_code: '', country_of_origin: '',
  unit_cost: 0, discount_percent: 0, tax_percent: 0,
  // Optional per-SKU-line packing details (Rishabh 2026-09-16). Blank by default so the
  // collapsible "Packing details" section shows empty until an operator fills one in.
  cartons: '', per_case: '', net_weight_kg: '', gross_weight_kg: '',
};

// Stable client-side row identity. React was keying line rows on the array
// index, which — combined with a stale-closure handleUpdateItem — let sibling
// edits desync (one line's value landing on another) and fed the backend
// scramble bug. New/blank rows get a `_uid`; loaded rows already carry a DB
// `id`. The row key uses `id ?? _uid`, so identity is stable across
// add/remove/reorder. Minted per-creation (NOT on the shared EMPTY_ITEM const,
// which every row would then share).
let _itemUidSeq = 0;
const nextItemUid = () => `it_${Date.now().toString(36)}_${(_itemUidSeq++).toString(36)}`;
const makeEmptyItem = () => ({ ...EMPTY_ITEM, _uid: nextItemUid() });

const EMPTY_FORM = {
  transfer_number: '',
  from_warehouse_code: '',
  to_warehouse_code: '',
  from_subsidiary_id: 0,
  to_subsidiary_id: 0,
  brand_id: 0,
  // Blank by default (Rishabh 2026-06-19): the first picked SKU drives Currency
  // (and Brand) via /api/stock-levels/sku-origin. Pre-defaulting to 'USD' showed
  // a misleading currency before any SKU was chosen.
  currency: '',
  transfer_date: new Date().toISOString().split('T')[0],
  dispatch_date: '',
  expected_arrival_date: '',
  completed_date: '',
  carrier: '',
  tracking_number: '',
  po_number: '', // links transfer to its Purchase Order (searchable from list)
  drive_link: '',
  shipping_method: '',
  transport_mode: '',
  incoterms: '',
  items: [makeEmptyItem()],
  freight_cost: 0,
  customs_duty: 0,
  landed_cost_method: 'by_value',
  notes: '',
  customer_notes: '',
  description_of_goods: '',
  cartons: '',
  per_case: '',
  gross_weight_kg: '',
  net_weight_kg: '',
  // Whole-transfer pallet details (Rishabh 2026-09-16) — printed on the packing list.
  pallet_count: '',
  pallet_dimensions: '',
  pallet_weight_kg: '',
  // Per-transfer TO-side ad-hoc override. 5 columns already on the table; this
  // wires them up. Empty = use the master warehouse address. The fields mirror
  // the 5 columns: no state, no address_line2 (the existing schema's choice).
  override_consignee_name: '',
  override_consignee_address_line1: '',
  override_consignee_city: '',
  override_consignee_country: '',
  override_consignee_postal_code: '',
  // Per-transfer FROM-side (shipper) ad-hoc override. Mirrors consignee.
  override_shipper_name: '',
  override_shipper_address_line1: '',
  override_shipper_city: '',
  override_shipper_country: '',
  override_shipper_postal_code: '',
  override_shipper_contact_name: '',
  override_shipper_contact_phone: '',
  override_shipper_contact_email: '',
};

// One visual row per SKU for a transfer — DRAFT as well as LOCKED (in transit /
// completed). The same SKU can sit on several warehouse_transfer_items rows: the
// allocator splits a request across batches (TRF-1102: SS0000452040 = 6,725 on
// its GR batch + 3,000 "Other stock"), and an auto-built / imported draft carries
// one row per source batch (TRF-1115: KF-Hairspray across 3 batches). Operators
// want ONE line per SKU with the split as sub-detail — "in the view part it
// should only be shown as 1 everywhere" (Rishabh 2026-09-08, reaffirmed
// 2026-09-16). Display only: the form state keeps the underlying DB rows (the save
// payload carries them unchanged, preserving batch traceability). A grouped line on
// an OPEN (draft) transfer stays EDITABLE — its value fields route through writeGroup,
// which spreads each edit across the underlying batch rows (a qty change via
// distributeQuantity) so the split stays consistent (Rishabh 2026-09-16). On a LOCKED
// transfer the whole line is read-only (rowLocked). Only the SKU field is read-only on
// a merged line either way (a SKU change needs per-row autofill that doesn't fit a
// summed line — remove the group and re-add instead).
//
// Only PERSISTED, UNPINNED rows merge: a real DB id, a SKU, and source_batch_id <= 0.
// A freshly-added row (no id) keeps its own editable row so live entry never locks or
// merges mid-type; a blank row (no SKU) and a batch-PINNED row (source_batch_id > 0,
// which deducts from that specific lot on ship) also stay separate. Locked transfers
// are unaffected — their rows are persisted, so the id gate is a no-op there.
function groupTransferLines(items) {
  const groups = [];
  const byKey = new Map();
  (items || []).forEach((it, index) => {
    const sku = String(it.sku || '').trim().toUpperCase();
    // Merge only persisted, UNPINNED rows: a real DB id (auto-increment >= 1), a SKU, and
    // source_batch_id <= 0. A new/blank row (no id) or a batch-PINNED row (source_batch_id > 0,
    // which deducts from that specific lot on ship) gets its own key and stays a separate,
    // per-row editable line — never folded into a summed group.
    const key = (Number(it.id) > 0 && sku && Number(it.source_batch_id) <= 0) ? `SKU:${sku}` : `__row_${index}`;
    if (!byKey.has(key)) {
      byKey.set(key, { rows: [], indices: [] });
      groups.push(key);
    }
    byKey.get(key).rows.push(it);
    byKey.get(key).indices.push(index);
  });
  return groups.map((key) => {
    const { rows, indices } = byKey.get(key);
    const first = rows[0];
    if (rows.length === 1) return { ...first, _index: indices[0] };
    const sum = (field) => rows.reduce((acc, r) => acc + (Number(r[field]) || 0), 0);
    const qty = sum('quantity');
    const costWeighted = rows.reduce((acc, r) => acc + (Number(r.quantity) || 0) * (Number(r.unit_cost) || 0), 0);
    return {
      ...first,
      _index: indices[0],
      _indices: indices,   // every underlying row, so a delete removes the whole SKU group
      quantity: qty,
      quantity_shipped: first.quantity_shipped === '' ? '' : sum('quantity_shipped'),
      quantity_received: sum('quantity_received'),
      discrepancy_qty: sum('discrepancy_qty'),
      unit_cost: qty > 0 ? Math.round((costWeighted / qty) * 10000) / 10000 : (Number(first.unit_cost) || 0),
      _split: rows.map((r) => ({ batch_number: r.batch_number, quantity: Number(r.quantity) || 0 })),
    };
  });
}

// Spread a new TOTAL quantity back across a grouped SKU's underlying batch rows,
// keeping their proportions so the per-batch rows still sum EXACTLY to the total
// (the last row absorbs any rounding remainder). Whole units when every input is
// whole (transfer qtys usually are), else 2dp. Editing a merged line's Qty routes
// here so no single batch row is left holding a wrong number and the sum stays right.
// (Batches on these grouped drafts are cosmetic labels — source_batch_id 0, ship
// re-FIFOs — so which batch absorbs the change carries no inventory meaning.)
function distributeQuantity(current, newTotal) {
  const n = current.length;
  const nt = Number(newTotal) || 0;
  if (n === 0) return [];
  const old = current.map((q) => Number(q) || 0);
  if (n === 1) return [Math.round(nt * 100) / 100];
  if (nt <= 0) return new Array(n).fill(0);
  const oldTotal = old.reduce((a, b) => a + b, 0);
  const whole = Number.isInteger(nt) && old.every((q) => Number.isInteger(q));
  const round = whole ? Math.round : (x) => Math.round(x * 100) / 100;
  const out = new Array(n);
  if (oldTotal <= 0) { out.fill(0); out[0] = round(nt); return out; }
  let running = 0;
  for (let i = 0; i < n - 1; i++) { out[i] = round(old[i] * nt / oldTotal); running += out[i]; }
  out[n - 1] = round(nt - running);
  // A batch row that rounds to 0 fails the server's "Quantity must be positive" check,
  // so when there are at least as many whole units as rows, give each row 1 from the largest.
  if (whole && nt >= n) {
    for (let i = 0; i < n; i++) {
      // While a row is short the largest row holds >= 2, so taking 1 never drops it below 1.
      while (out[i] < 1) { const big = out.indexOf(Math.max(...out)); out[big] -= 1; out[i] += 1; }
    }
  }
  return out;
}

export default function WarehouseTransferEntry() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const isEdit = !!routeId;
  // Back/Cancel returns to the FILTERED transfer list the operator came from
  // (Rishabh 2026-07-02). The list stashes its filter query-string in
  // sessionStorage. (Post-SAVE still opens the transfer detail — that's the
  // deliberate "show the created/edited transfer" behavior Rishabh asked for.)
  const listBackTo = `/inventory/transfers${(() => { try { return sessionStorage.getItem('scm.transfersListSearch') || ''; } catch (_) { return ''; } })()}`;
  const { selectedBrand } = useBrand();
  const { subsidiaries: subList } = useSubsidiary();

  // Shared MUI confirm — same primitive as the PO screens, replacing the
  // native window.confirm on line removal.
  const { confirmDialogProps, confirm } = useConfirmDialog();

  // Unsaved-changes tracking. Same approach as PurchaseOrderEntry: listen for
  // real DOM change events bubbling out of the form rather than deep-comparing
  // state, because several effects here auto-fill dates/costs after load and a
  // snapshot compare would read dirty immediately and nag on every exit.
  const [userTouched, setUserTouched] = useState(false);
  const markTouched = useCallback(() => setUserTouched(true), []);
  const guardedLeave = useUnsavedChangesGuard(userTouched, confirm);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Non-blocking: a receive completed but the server skipped some destination cost rows (cost_rows_skipped, W20).
  const [warning, setWarning] = useState(null);
  // P3.1: per-step save indicator. Save can be a sequence of 1-3 backend calls
  // (update → optional ship → optional receive). Pre-fix, only the final
  // outcome surfaced as a single toast; sub-step failures were console.warn'd
  // and silently swallowed. Operators had no signal "the ship went through but
  // receive timed out". Now each step's lifecycle is visible inline.
  const [saveSteps, setSaveSteps] = useState([]); // [{key,label,status,error}]
  // P5.4: in-page PDF preview modal. Replaces the previous "click → download
  // PDF blind" flow. Operator gets to verify the doc before saving/sending.
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', kind: '', filename: '', loading: false });
  const openPdfPreview = async (kind) => {
    // No confirm here — Rishabh 2026-05-21 (round 2): the toolbar button on
    // the transfer edit page is a "view the PDF" verb, not a deliberate
    // "issue" event. The CI gets created/reused silently as a side-effect of
    // rendering (the renderer needs a CI row to read from). The deliberate
    // "issue" confirm still fires from the Transfers list-page action menu.
    setPdfPreview({ open: true, url: '', kind, filename: '', loading: true });
    try {
      const r = await api.post(`/api/commercial-invoices/from-transfer/${routeId}`);
      const ciId = r?.data?.data?.id;
      if (!ciId) throw new Error('Commercial invoice id missing in response');
      const pdfResp = await api.get(`/api/commercial-invoices/${ciId}/pdf?kind=${kind}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([pdfResp.data], { type: 'application/pdf' }));
      const filename = `${kind === 'invoice' ? 'CommercialInvoice' : 'PackingList'}-${transferForm.transfer_number || routeId}.pdf`;
      setPdfPreview({ open: true, url, kind, filename, loading: false });
    } catch (e) {
      setPdfPreview({ open: false, url: '', kind: '', filename: '', loading: false });
      setError(extractApiError(e, `${kind === 'invoice' ? 'Commercial invoice' : 'Packing list'} generation failed`));
    }
  };
  const closePdfPreview = () => {
    if (pdfPreview.url) URL.revokeObjectURL(pdfPreview.url);
    setPdfPreview({ open: false, url: '', kind: '', filename: '', loading: false });
  };
  const downloadPdfPreview = () => {
    if (!pdfPreview.url) return;
    const a = document.createElement('a');
    a.href = pdfPreview.url; a.download = pdfPreview.filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const [transferForm, setTransferForm] = useState(EMPTY_FORM);
  // Seeded with the loaded transfer's dispatch_date/completed_date so the
  // auto-fill effects below don't fire on initial mount and clobber per-line
  // server-side qty_shipped / qty_received (e.g. an 80-of-100 partial ship).
  const prevDispatchDateRef = useRef('');
  const prevCompletedDateRef = useRef('');
  const [warehouses, setWarehouses] = useState([]);
  const [supplierWhCodes, setSupplierWhCodes] = useState(new Set());
  const [brandsList, setBrandsList] = useState([]);
  const [whSubMappings, setWhSubMappings] = useState([]);
  const [existingStatus, setExistingStatus] = useState(null);
  const [skuSummary, setSkuSummary] = useState([]);
  // Per-line typed text for the SKU picker, kept SEPARATE from item.sku so that
  // typing drives the Autocomplete filter (narrow-on-type) WITHOUT committing the
  // SKU on every keystroke — committing runs handleUpdateItem which fires the
  // /sku-origin API + cost/batch fills. We only commit on select (onChange) or
  // blur. Keyed by line index; falls back to item.sku when untouched.
  const [batchOptionsBySku, setBatchOptionsBySku] = useState({});
  // Which line's Unit Cost input is focused — so we can show the rounded 2dp value
  // when idle (0.2198 → 0.22) but the FULL value while editing. The stored value +
  // all calcs keep full precision. (#8, Rishabh 2026-07-01.)
  const [costFocusIdx, setCostFocusIdx] = useState(null);
  // "<from_warehouse>|<SKU uppercased>" keys whose auto-cost lookup found no cost AT
  // the factory they ship from — the line shows "Book the manufacture receipt first". Kept outside the
  // item objects so it never rides into the save payload (items are spread on save).
  const [factoryNoCostSkus, setFactoryNoCostSkus] = useState({});
  const [skuMetaCache, setSkuMetaCache] = useState({});
  // Which lines have their optional "Packing details" section expanded. Keyed by the
  // stable row key (id ?? _uid) so it survives re-render; collapsed by default so the
  // line stays clean (Rishabh 2026-09-16 wanted per-SKU packing fields without clutter).
  const [packingOpenKeys, setPackingOpenKeys] = useState(() => new Set());
  const togglePacking = (k) => setPackingOpenKeys(prev => {
    const n = new Set(prev);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  // Pre-ship stock-shortage guard dialog (Rishabh 2026-06-19): blocks a ship/close
  // when any line exceeds available stock, with a per-SKU shortage breakdown.
  const [shortageDialog, setShortageDialog] = useState({ open: false, lines: [], pendingMode: 'back' });

  const isSupplierSourceTransfer = useMemo(
    () => transferForm.from_warehouse_code && supplierWhCodes.has(transferForm.from_warehouse_code),
    [transferForm.from_warehouse_code, supplierWhCodes]
  );

  // P3.3: map ISO-4217 codes → symbols for compact numeric input prefix.
  // Falls back to the code itself for currencies without a common symbol.
  const currencySymbol = (() => {
    const map = { USD: '$', GBP: '£', EUR: '€', AUD: 'A$', SGD: 'S$', INR: '₹', CNY: '¥', CAD: 'C$', NZD: 'NZ$' };
    return map[transferForm.currency] || transferForm.currency || '$';
  })();

  // P3.2: per-item validation flags so the operator sees WHICH line is broken
  // without parsing a top-level error. SKU + quantity are the two required
  // fields server-side (routes.js POST validators). Other fields are optional.
  const itemValidation = useMemo(() => transferForm.items.map(it => ({
    missingSku: !it.sku || !String(it.sku).trim(),
    missingQty: !Number(it.quantity) || Number(it.quantity) <= 0,
  })), [transferForm.items]);
  const invalidItemCount = itemValidation.filter(v => v.missingSku || v.missingQty).length;

  // Augment the active-warehouses list with any code referenced on the
  // existing transfer that isn't currently active (e.g. legacy 'GIT' goods-
  // in-transit pseudo-warehouse). Pre-fix the Autocomplete rendered EMPTY for
  // archived codes because `warehouses.find(...)` returned undefined; operator
  // saw a blank required field on a saved transfer and assumed it was broken.
  // Now the missing code surfaces as a clearly-labelled "(archived)" entry.
  const warehouseOptions = useMemo(() => {
    const referenced = [transferForm.from_warehouse_code, transferForm.to_warehouse_code]
      .filter(Boolean);
    if (referenced.length === 0) return warehouses;
    const known = new Set(warehouses.map(w => w.code));
    const archived = referenced
      .filter(c => !known.has(c))
      .filter((c, i, arr) => arr.indexOf(c) === i)
      .map(code => ({ code, name: '(archived)', city: '', country: '', is_active: false, _archived: true }));
    return archived.length ? [...warehouses, ...archived] : warehouses;
  }, [warehouses, transferForm.from_warehouse_code, transferForm.to_warehouse_code]);

  // 2026-05-19: lock disabled in favor of backend-side safety. The backend
  // now (a) always persists logistics fields via the logistics-only update
  // path, and (b) detects structural changes (items / warehouse codes /
  // transfer_date) and only then reverses inventory + reverts status to draft.
  // Locking the UI was creating user-facing problems with no upside —
  // operators couldn't update carrier / tracking / dates after dispatch.
  // Keeping the variable as a named no-op so re-tightening is a one-line flip.
  const lockNonDraftFields = false;
  // Edit-lock (Rishabh 2026-09-08, TRF-1102; job #424): once a transfer is in
  // transit or completed its stock has moved, so the LINES (SKU, quantities,
  // batch, add/remove), the warehouses and the transfer date are read-only. The
  // backend refuses such an edit with TRANSFER_LOCKED (409) anyway; the form
  // says so up front. Reopening is explicit and the reverse steps the form
  // already runs: clear the Completed Date and/or Dispatch Date, Save
  // (reverse-receive / reverse-ship move the stock back), then edit the Open
  // transfer. Logistics fields keep saving in place at any status.
  const linesLocked = isEdit && (existingStatus === 'completed' || existingStatus === 'in_transit');

  // Initial load — warehouses + suppliers + brands + whSubMappings, plus (in edit mode) the existing transfer.
  useEffect(() => {
    (async () => {
      try {
        const [whRes, supRes, brRes, wsmRes] = await Promise.all([
          getWarehouses({ is_active: true }),
          getSuppliers({ is_active: true, limit: 500 }),
          brandService.getBrands(),
          getBrandSubsidiaryMappings(),
        ]);
        setWarehouses(whRes?.warehouses || whRes?.data || []);
        const sList = Array.isArray(supRes?.data) ? supRes.data : (supRes?.data?.suppliers || []);
        setSupplierWhCodes(new Set(sList.map(s => s.default_warehouse_code).filter(Boolean)));
        const bList = Array.isArray(brRes) ? brRes : (brRes?.data || brRes?.brands || []);
        setBrandsList(bList.filter(x => x.id !== 1));
        setWhSubMappings(wsmRes?.data || []);
        if (isEdit) {
          const tRes = await getTransfer(routeId);
          const t = tRes?.transfer || tRes?.data;
          if (!t) throw new Error('Transfer not found');
          setExistingStatus(t.status);
          // Dispatch/Completed dates — previously dropped on reload, so an
          // operator re-entering the edit page saw both dates empty even when
          // they were stored in DB (dispatch_date column on warehouse_transfers,
          // actual_arrival_date column = the "completed" date). Without these,
          // the auto-fill effects would also re-fire on the next user edit and
          // clobber the server's per-line qty_shipped / qty_received values.
          const loadedDispatch = (t.dispatch_date || '').toString().slice(0, 10);
          const loadedCompleted = (t.actual_arrival_date || '').toString().slice(0, 10);
          // Gate the "blank Dispatched on completion" UX on status === 'completed'
          // and NOT on actual_arrival_date presence — backend's receive endpoint
          // stamps the date even on partial receives (keep_in_transit=true), so
          // a partial-receive transfer is in_transit + date set. In that mixed
          // state there's still qty in transit, so Dispatched must keep showing.
          const isCompletedLoad = t.status === 'completed';
          setTransferForm({
            ...EMPTY_FORM,
            // Carry the human transfer number so the page title reads
            // "Edit Transfer TRF-1079" instead of the numeric route id.
            transfer_number: t.transfer_number || '',
            from_warehouse_code: t.from_warehouse_code || '',
            to_warehouse_code: t.to_warehouse_code || '',
            from_subsidiary_id: Number(t.from_subsidiary_id || 0),
            to_subsidiary_id: Number(t.to_subsidiary_id || 0),
            brand_id: Number(t.brand_id || 0),
            currency: t.currency || 'USD',
            transfer_date: (t.transfer_date || '').toString().slice(0, 10) || EMPTY_FORM.transfer_date,
            dispatch_date: (loadedDispatch && loadedDispatch !== '1970-01-01') ? loadedDispatch : '',
            completed_date: isCompletedLoad ? loadedCompleted : '',
            expected_arrival_date: (t.expected_arrival_date || '').toString().slice(0, 10) || '',
            carrier: t.carrier || '',
            tracking_number: t.tracking_number || '',
            po_number: t.po_number || '',
            drive_link: t.drive_link || '',
            shipping_method: t.shipping_method || '',
            transport_mode: t.transport_mode || '',
            incoterms: t.incoterms || '',
            notes: t.notes || '',
            customer_notes: t.customer_notes || '',
            description_of_goods: t.description_of_goods || '',
            cartons: t.cartons || '',
            per_case: t.per_case || '',
            gross_weight_kg: t.gross_weight_kg || '',
            net_weight_kg: t.net_weight_kg || '',
            // Whole-transfer pallet details (blank if 0/empty so the fields read empty).
            pallet_count: Number(t.pallet_count || 0) || '',
            pallet_dimensions: t.pallet_dimensions || '',
            pallet_weight_kg: Number(t.pallet_weight_kg || 0) || '',
            // RC2 (Rishabh round 2): load freight + customs costs that were
            // previously dropped — the form initialized them at 0 on every
            // reopen even when the operator had saved real values.
            freight_cost: Number(t.freight_cost || 0) || 0,
            customs_duty: Number(t.customs_duty || 0) || 0,
            landed_cost_method: t.landed_cost_method || 'by_value',
            override_consignee_name: t.override_consignee_name || '',
            override_consignee_address_line1: t.override_consignee_address_line1 || '',
            override_consignee_city: t.override_consignee_city || '',
            override_consignee_country: t.override_consignee_country || '',
            override_consignee_postal_code: t.override_consignee_postal_code || '',
            override_shipper_name: t.override_shipper_name || '',
            override_shipper_address_line1: t.override_shipper_address_line1 || '',
            override_shipper_city: t.override_shipper_city || '',
            override_shipper_country: t.override_shipper_country || '',
            override_shipper_postal_code: t.override_shipper_postal_code || '',
            override_shipper_contact_name: t.override_shipper_contact_name || '',
            override_shipper_contact_phone: t.override_shipper_contact_phone || '',
            override_shipper_contact_email: t.override_shipper_contact_email || '',
            // Bug 2 (Rishabh): once a transfer is received (completed_date set),
            // operators expect Qty Dispatched to be empty — the units have
            // "moved" from Dispatch to Transferred. DB still stores the
            // historical quantity_shipped (used for discrepancy reporting and
            // landed-cost calc), this is purely a display blank-out on the
            // entry form so the column reflects current state, not history.
            items: (t.items || []).map(i => ({
              id: i.id,
              // source_batch_id > 0 = pinned to a real inventory_batches lot (ship deducts
              // from THAT lot). Loaded so groupTransferLines never merges a pinned row —
              // only unpinned (source_batch_id 0, a cosmetic batch label; ship re-FIFOs) rows
              // collapse into one editable line.
              source_batch_id: Number(i.source_batch_id || 0),
              sku: i.sku || '',
              batch_number: i.batch_number || '',
              quantity: Number(i.quantity_requested ?? i.quantity ?? 0) || '',
              quantity_shipped: isCompletedLoad ? '' : (Number(i.quantity_shipped || 0) || ''),
              quantity_received: Number(i.quantity_received || 0) || '',
              // Per-line discrepancy info populated by backend receiveTransfer
              // when received < shipped. P5.2 surfaces this as a "N units
              // short" callout with reason on hover.
              discrepancy_qty: Number(i.discrepancy_qty || 0) || 0,
              discrepancy_reason: i.discrepancy_reason || '',
              // hs_code/etc — see EMPTY_ITEM for full shape
              hs_code: i.hs_code || '',
              country_of_origin: i.country_of_origin || '',
              unit_cost: Number(i.unit_cost || 0),
              discount_percent: Number(i.discount_percent || 0),
              tax_percent: Number(i.tax_percent || 0),
              // Per-SKU-line packing details (blank if 0 so the optional fields read empty).
              cartons: Number(i.cartons || 0) || '',
              per_case: Number(i.per_case || 0) || '',
              net_weight_kg: Number(i.net_weight_kg || 0) || '',
              gross_weight_kg: Number(i.gross_weight_kg || 0) || '',
              product_name: i.product_name || '',
            })),
          });
          // Seed auto-fill guard refs with the loaded values — without this
          // the auto-fill effects would fire on the very first render after
          // load (ref starts as '', loaded date is non-empty → wasEmpty=true
          // → clobbers the authoritative per-line qty_shipped / qty_received).
          // Mirror the same blank values we set in form state above so the
          // guard `prev === transferForm.x` check returns early on mount.
          prevDispatchDateRef.current = (loadedDispatch && loadedDispatch !== '1970-01-01') ? loadedDispatch : '';
          prevCompletedDateRef.current = isCompletedLoad ? loadedCompleted : '';
        }
      } catch (e) {
        setError(extractApiError(e, 'Failed to load form data'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // Default subsidiaries to the company is_default when subList loads (create mode only).
  useEffect(() => {
    if (isEdit) return;
    if (!subList || subList.length === 0) return;
    const def = subList.find(s => s.is_default && s.is_active !== false);
    if (!def) return;
    setTransferForm(prev => ({
      ...prev,
      from_subsidiary_id: prev.from_subsidiary_id || Number(def.id),
      to_subsidiary_id: prev.to_subsidiary_id || Number(def.id),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subList, isEdit]);

  useEffect(() => {
    // Brand starts BLANK on new transfers (Rishabh 2026-06-19). We deliberately
    // do NOT pre-fill from the app-header brand (selectedBrand): on a transfer
    // OUT of a supplier/other-brand warehouse (e.g. Aurora → Allkme stock) the
    // header brand (e.g. SkinnyMint) mismatched the source, which both (a) showed
    // a wrong default and (b) made the brand-scoped SKU/stock picker return
    // nothing ("NO STOCK HERE"). The first picked SKU now drives Brand + Currency
    // via /api/stock-levels/sku-origin (handleUpdateItem 'sku' branch below).
    // Effect retained (no-op) only so selectedBrand stays referenced.
    void selectedBrand?.id;
    void isEdit;
  }, [isEdit, selectedBrand?.id]);

  const resolveWarehouseSubsidiary = (warehouseCode, brandId) => {
    if (!warehouseCode) return null;
    const today = new Date().toISOString().slice(0, 10);
    if (brandId) {
      const m = whSubMappings.find(x =>
        x.warehouse_code === warehouseCode &&
        Number(x.brand_id) === Number(brandId) &&
        x.is_active &&
        (!x.effective_from || x.effective_from <= today) &&
        (!x.effective_to || x.effective_to >= today)
      );
      if (m && Number(m.subsidiary_id) > 0) return Number(m.subsidiary_id);
    }
    const generic = whSubMappings.find(x =>
      x.warehouse_code === warehouseCode &&
      (Number(x.brand_id) === 0 || !x.brand_id) &&
      x.is_active
    );
    if (generic && Number(generic.subsidiary_id) > 0) return Number(generic.subsidiary_id);
    const wh = warehouses.find(w => w.code === warehouseCode);
    if (wh && wh.subsidiary_id && Number(wh.subsidiary_id) > 0) return Number(wh.subsidiary_id);
    return null;
  };

  useEffect(() => {
    if (!transferForm.from_warehouse_code) return;
    const resolved = resolveWarehouseSubsidiary(transferForm.from_warehouse_code, transferForm.brand_id);
    if (resolved) setTransferForm(prev => ({ ...prev, from_subsidiary_id: resolved }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferForm.from_warehouse_code, transferForm.brand_id, warehouses.length, whSubMappings.length]);

  useEffect(() => {
    if (!transferForm.to_warehouse_code) return;
    const resolved = resolveWarehouseSubsidiary(transferForm.to_warehouse_code, transferForm.brand_id);
    if (resolved) setTransferForm(prev => ({ ...prev, to_subsidiary_id: resolved }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferForm.to_warehouse_code, transferForm.brand_id, warehouses.length, whSubMappings.length]);

  // Auto-populate Qty Dispatched / Qty Transferred when the operator enters
  // the corresponding header date — matches the CIN7 mental model where
  // setting a milestone date implies "all lines moved through this milestone".
  // Operator can still manually edit per-row afterwards for partial shipments
  // / discrepancies. Only fires when the date goes from empty → non-empty so
  // we don't clobber a previously-typed per-row value on re-save.
  //
  // Refs declared at the top of the component; seeded by the load effect on
  // isEdit so the auto-fill below doesn't fire on initial mount.
  useEffect(() => {
    const wasEmpty = !prevDispatchDateRef.current;
    const isSet = !!transferForm.dispatch_date;
    const prev = prevDispatchDateRef.current;
    prevDispatchDateRef.current = transferForm.dispatch_date;
    if (prev === transferForm.dispatch_date) return;
    // Forward transition (empty → set): auto-populate Qty Dispatched from Qty.
    if (wasEmpty && isSet) {
      setTransferForm(p => ({
        ...p,
        items: p.items.map(it => ({
          ...it,
          quantity_shipped: it.quantity_shipped || it.quantity || '',
        })),
      }));
      return;
    }
    // RC4 (Rishabh round 2): reverse transition (set → empty). Removing the
    // Dispatch Date should clear Qty Dispatched on every line, mirroring the
    // forward auto-fill. Status revert is handled on save by the backend
    // reverse-ship route call from handleSave.
    if (!wasEmpty && !isSet) {
      setTransferForm(p => ({
        ...p,
        items: p.items.map(it => ({ ...it, quantity_shipped: '' })),
      }));
    }
  }, [transferForm.dispatch_date]);
  useEffect(() => {
    const wasEmpty = !prevCompletedDateRef.current;
    const isSet = !!transferForm.completed_date;
    const prev = prevCompletedDateRef.current;
    prevCompletedDateRef.current = transferForm.completed_date;
    if (prev === transferForm.completed_date) return;
    // Forward (empty → set): mark units as transferred. Bug 2 fix.
    // An In Transit transfer being received keeps its Qty Dispatched: the goods already
    // left, and the shipped figure is what a short receipt is measured against (TRF-1125).
    if (wasEmpty && isSet) {
      const keepShipped = isEdit && existingStatus === 'in_transit';
      setTransferForm(p => ({
        ...p,
        items: p.items.map(it => ({
          ...it,
          quantity_received: it.quantity_received || it.quantity_shipped || it.quantity || '',
          quantity_shipped: keepShipped ? it.quantity_shipped : '',
        })),
      }));
      return;
    }
    // RC1 (Rishabh round 2): reverse (set → empty). Clearing Completed Date
    // un-marks receipt — quantity_received drops to 0; quantity_shipped is
    // restored from the prior quantity_received value so units flow back to
    // the Dispatched column. Status revert handled by handleSave below.
    if (!wasEmpty && !isSet) {
      setTransferForm(p => ({
        ...p,
        items: p.items.map(it => ({
          ...it,
          quantity_shipped: it.quantity_shipped || it.quantity_received || '',
          quantity_received: '',
        })),
      }));
    }
    // isEdit/existingStatus only pick the forward branch's keepShipped; a re-run without a
    // date change returns at the prev === completed_date guard above.
  }, [transferForm.completed_date, isEdit, existingStatus]);

  // Stock levels for the SKU picker. Fetched on source-warehouse / brand change AND
  // explicitly re-called after save (handleSave) so reversed ship / receive
  // operations show the restored source qty immediately — without this, the
  // per-line Available indicator + dropdown badges keep rendering pre-reversal
  // numbers, surfacing phantom "Out of Stock" right after the operator clears
  // a Dispatch / Completed Date (Rishabh 2026-05-20).
  const stockFetchSeq = useRef(0);
  // The from-warehouse on screen now (DT-989). A per-SKU batch read captures the warehouse it was asked for and is
  // dropped if this has changed by the time it answers. Not stockFetchSeq: picking the first SKU can auto-set the
  // brand, which bumps that sequence for the SAME warehouse and would drop the SKU's own read.
  const fromWarehouseRef = useRef(transferForm.from_warehouse_code);
  fromWarehouseRef.current = transferForm.from_warehouse_code;
  const fetchAvailableStock = useRef(null);
  fetchAvailableStock.current = async () => {
    // Generation guard (Rishabh 2026-06-22): the all-brands catalog fetch (brand
    // blank) is uncached and can resolve AFTER a subsequently-selected brand's
    // (often cache-hit) stock fetch, overwriting the brand-filtered list with ALL
    // brands. Stamp each invocation; a stale (superseded) fetch must not setState.
    const seq = ++stockFetchSeq.current;
    const stale = () => seq !== stockFetchSeq.current;
    if (!transferForm.from_warehouse_code) {
      setSkuSummary([]);
      setBatchOptionsBySku({});
      return;
    }
    // Brand blank ("All brands / cross-brand"): the stock-levels engine REQUIRES a
    // brand (backend cross-brand-contamination guard), so we can't load per-warehouse
    // quantities yet. Populate the SKU picker from the all-brands product catalog so
    // the operator can find/type a SKU WITHOUT first choosing a brand — picking one
    // resolves Brand+Currency via sku-origin, which re-runs this effect with real
    // quantities. Options carry stockUnknown so we don't render a false "out of stock".
    // (Rishabh 2026-06-22 — SKU should surface from the warehouse, not require a brand.)
    if (!Number(transferForm.brand_id || 0)) {
      try {
        const skuRes = await api.get('/api/stock-levels/inventory-skus', {
          params: { active_only: 1 },
        });
        if (stale()) return;
        const list = skuRes?.data?.data || skuRes?.data || [];
        setSkuSummary((Array.isArray(list) ? list : [])
          .map(s => ({
            sku: s.primary_sku || '',
            product_name: s.product_name || '',
            image_url: '',
            thumbnail_url: '',
            total_available: null,
            batch_count: 0,
            stockUnknown: true,
          }))
          .filter(o => o.sku));
        setBatchOptionsBySku({});
      } catch (e) {
        console.error('[WarehouseTransferEntry] Failed to load catalog SKUs:', e);
        if (!stale()) { setSkuSummary([]); setBatchOptionsBySku({}); }
      }
      return;
    }
    // SEED FIRST, DECORATE AFTER (Vishal 2026-07-16: "SKU should be picked first, the
    // quantity can be calculated later if required"). The stock engine takes ~8s (p50
    // 7.5s measured) to recompute every SKU's on-hand at this warehouse from raw
    // movements. Blocking the picker on it meant the operator stared at an empty
    // dropdown for ~8s after choosing a warehouse — waiting on a number that decides
    // nothing HERE, since allocateFIFOBatches is what gates the transfer at save.
    // So: catalog first (typeable in ~50ms), real quantities when they arrive.
    //
    // AMENDED 2026-07-16: this used to add "since allocateFIFOBatches re-derives
    // availability fresh at save" — it no longer does. This fetch now WARMS the cache
    // entry the save path reuses (same key: no sku, same warehouse, same limit/offset),
    // which is what took transfer creation from 70.5s to ~5s. So this call is no longer
    // display-only: it is also the prefetch for the gate. Keep its params in step with
    // PICKER_STOCK_PAGE_LIMIT in backend/warehouse-transfers/service.js.
    // Hoisted so the stock fetch below can MERGE quantities into it rather than
    // replacing it with in-stock-only (which used to hide zero-stock SKUs — see
    // mergeCatalogWithStock). Empty on catalog-fetch failure → merge degrades to
    // stock-only, the pre-existing fallback.
    let catalogOptions = [];
    try {
      const catalogRes = await api.get('/api/stock-levels/inventory-skus', {
        params: { brand_id: Number(transferForm.brand_id), active_only: 1 },
      });
      if (stale()) return;
      catalogOptions = catalogToSkuOptions(catalogRes?.data?.data || catalogRes?.data || []);
      setSkuSummary(catalogOptions);
    } catch (e) {
      // Non-fatal: the stock fetch below still populates the picker, just slower.
      console.warn('[WarehouseTransferEntry] catalog seed failed, falling back to stock-only:', e.message);
    }
    // In transit / completed: the stock has already left the source, so the
    // per-line "Available at <warehouse> / OOS" check would only ever say OOS
    // (Rishabh 2026-09-08, TRF-1102). It is pre-ship information; skip the
    // engine + batch reads and leave the catalog names in place.
    if (linesLocked) {
      setBatchOptionsBySku({});
      return;
    }
    try {
      const params = buildStockLevelParams(transferForm.from_warehouse_code, transferForm.brand_id);
      const batchParams = { limit: 1000, warehouse_code: transferForm.from_warehouse_code };
      if (Number(transferForm.brand_id) > 0) batchParams.brand_id = Number(transferForm.brand_id);
      const [stockRes, batchRes] = await Promise.all([
        getCalculatedStockLevels(params),
        getInventoryBatches(batchParams),
      ]);
      if (stale()) return;
      // MERGE quantities into the full catalog (keeps zero-stock SKUs selectable) rather
      // than replacing with in-stock-only. Lets an operator build a draft for a finished
      // good still on an open manufacture PO; SHIP still gates on real on-hand.
      setSkuSummary(mergeCatalogWithStock(catalogOptions, stockRes?.data?.items || [], transferForm.from_warehouse_code));
      const batches = batchRes?.data?.batches || batchRes?.batches || [];
      const batchesBySku = {};
      for (const batch of batches) {
        if (!batch?.sku) continue;
        if (!batchesBySku[batch.sku]) batchesBySku[batch.sku] = [];
        batchesBySku[batch.sku].push(batch);
      }
      setBatchOptionsBySku(batchesBySku);
    } catch (e) {
      console.error('[WarehouseTransferEntry] Failed to load stock levels:', e);
      // Deliberately do NOT clear skuSummary here: the catalog seed above is still a
      // usable picker, and the operator can complete the transfer without a quantity
      // hint (the gate validates at save). Wiping it would turn a degraded-but-working
      // form into an empty one. Only the batch options are unusable.
      if (!stale()) setBatchOptionsBySku({});
    }
  };
  // DT-989: until the new warehouse's batch read answers (the stock engine takes ~8s), the map still holds the previous
  // warehouse's batches; a SKU picked meanwhile skipped its own read and auto-picked one of them. Empty it on a change.
  useEffect(() => {
    setBatchOptionsBySku({});
  }, [transferForm.from_warehouse_code]);
  useEffect(() => {
    fetchAvailableStock.current && fetchAvailableStock.current();
  }, [transferForm.from_warehouse_code, transferForm.brand_id]);

  const fetchSkuMeta = async (sku) => {
    if (!sku) return;
    const key = sku.toUpperCase();
    if (skuMetaCache[key]) return;
    const stockOption = skuSummary.find(s => String(s.sku || '').toUpperCase() === key);
    setSkuMetaCache(prev => ({
      ...prev,
      [key]: {
        image_url: stockOption?.thumbnail_url || stockOption?.image_url || '',
        product_name: stockOption?.product_name || '',
        unit_cost: 0,
      },
    }));
  };

  useEffect(() => {
    const t = setTimeout(() => {
      for (const it of transferForm.items) {
        if (it.sku) fetchSkuMeta(it.sku);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferForm.items.map(i => i.sku).join('|')]);

  // Batch-race back-fill: if a SKU was selected before batchOptionsBySku
  // finished loading, handleUpdateItem's `(batchOptionsBySku[sku]||[])[0]`
  // lookup found nothing and the line got no batch_number. When the batch map
  // (re)loads, back-fill batch_number for any line that has a SKU but no batch
  // yet — without clobbering a batch the operator already chose. Lookup keying
  // matches handleUpdateItem (raw, database-native sku case).
  useEffect(() => {
    setTransferForm(prev => {
      let changed = false;
      const items = prev.items.map(it => {
        if (it.sku && !it.batch_number) {
          const bn = (batchOptionsBySku[it.sku] || [])[0]?.batch_number;
          if (bn) { changed = true; return { ...it, batch_number: bn }; }
        }
        return it;
      });
      return changed ? { ...prev, items } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchOptionsBySku]);

  const handleAddItem = () => {
    // Click-driven, so no DOM change event bubbles — mark dirty explicitly.
    markTouched();
    setTransferForm(prev => ({ ...prev, items: [...prev.items, makeEmptyItem()] }));
  };

  const handleRemoveItem = async (index) => {
    // P4.4: confirm before deleting a line that has real data on it. A blank
    // row removed by accident is recoverable (just re-add); a row with SKU
    // + qty isn't. Only the "has SKU AND non-zero qty" case prompts so the
    // common path (delete an unfilled extra row) stays one-click.
    const it = transferForm.items[index] || {};
    const hasRealData = !!it.sku && Number(it.quantity) > 0;
    if (hasRealData) {
      const ok = await confirm({
        title: `Remove line ${index + 1}?`,
        message: `${it.sku} — quantity ${it.quantity}. This only edits the form; nothing changes until you save the transfer.`,
        confirmText: 'Remove line',
        severity: 'warning',
      });
      if (!ok) return;
    }
    markTouched();
    // Functional updater: the confirm above is awaited, so the `transferForm`
    // captured in this closure can be stale by the time it resolves.
    setTransferForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  // Remove a whole grouped SKU line at once. The display merges a SKU's several
  // batch rows into one line (groupTransferLines); deleting that line must drop
  // ALL its underlying warehouse_transfer_items rows, or the other batches would
  // be orphaned in the save payload.
  const handleRemoveItemGroup = async (indices) => {
    const idxs = indices || [];
    const rows = idxs.map(i => transferForm.items[i]).filter(Boolean);
    const sku = rows[0]?.sku || '';
    const totalQty = rows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
    const ok = await confirm({
      title: `Remove ${sku}?`,
      message: `${sku} — ${rows.length} batch line${rows.length === 1 ? '' : 's'}, total quantity ${totalQty}. This only edits the form; nothing changes until you save the transfer.`,
      confirmText: 'Remove line',
      severity: 'warning',
    });
    if (!ok) return;
    markTouched();
    const drop = new Set(idxs);
    setTransferForm(prev => ({ ...prev, items: prev.items.filter((_, i) => !drop.has(i)) }));
  };

  // Write an edit made on a MERGED (grouped) line back to its underlying batch rows.
  // The line is one visual row for a SKU picked across N batches (groupTransferLines);
  // per-SKU fields (cost, disc, tax, hs_code, coo, description) apply identically to
  // every underlying row, and a quantity edit is spread across them (distributeQuantity)
  // so the batch rows still sum to the new total. Display-only grouping means the save
  // payload keeps every batch row — this just keeps them all consistent. (Single, non-
  // grouped lines never call this; they use handleUpdateItem directly, unchanged.)
  const writeGroup = (item, field, value) => {
    const idxs = item._indices || [];
    if (!idxs.length) return;
    markTouched();
    setTransferForm(prev => {
      const items = [...prev.items];
      if (field === 'quantity' || field === 'quantity_shipped' || field === 'quantity_received') {
        // Split by the rows' qty from BEFORE this edit started (kept on the row as _base_<field>),
        // not the running qty: typing 264 passes through 2 and 26, and re-splitting each
        // keystroke's result rounds a small batch to 0 for good (Rishabh 2026-10-26, TRF-1115).
        const baseKey = `_base_${field}`;
        const current = idxs.map(i => Number(items[i]?.[baseKey] ?? items[i]?.[field]) || 0);
        const dist = distributeQuantity(current, value);
        idxs.forEach((i, k) => {
          if (!items[i]) return;
          const patch = { [field]: dist[k], [baseKey]: current[k] };
          // Mirror handleUpdateItem's RC5 milestone sync per underlying row, so a grouped
          // line behaves exactly like a single line: a quantity edit propagates to
          // quantity_shipped (if dispatch_date set) and quantity_received (if completed_date
          // set, moving qty out of Dispatched). Completed => linesLocked => read-only, so
          // that branch is only reachable in theory, but kept for exact parity.
          if (field === 'quantity') {
            if (prev.dispatch_date) patch.quantity_shipped = dist[k];
            if (prev.completed_date) { patch.quantity_received = dist[k]; patch.quantity_shipped = ''; }
          }
          items[i] = { ...items[i], ...patch };
        });
      } else {
        idxs.forEach(i => { if (items[i]) items[i] = { ...items[i], [field]: value }; });
      }
      return { ...prev, items };
    });
  };

  // Leaving a merged line's qty box ends that edit: drop the _base_* snapshots so the next edit
  // splits from the qty now shown (other paths, e.g. the dispatch-date sync, may change it meanwhile).
  const endGroupEdit = (item) => {
    const idxs = item._indices || [];
    setTransferForm(prev => {
      if (!idxs.some(i => prev.items[i] && ('_base_quantity' in prev.items[i] || '_base_quantity_shipped' in prev.items[i] || '_base_quantity_received' in prev.items[i]))) return prev;
      const items = [...prev.items];
      idxs.forEach(i => {
        if (!items[i]) return;
        const { _base_quantity, _base_quantity_shipped, _base_quantity_received, ...rest } = items[i];
        items[i] = rest;
      });
      return { ...prev, items };
    });
  };

  // Lightweight per-keystroke SKU text update for the dropdown's type-to-narrow.
  // Writes ONLY item.sku (no /sku-origin, /cogs, or batch API calls) so typing
  // stays snappy and the list narrows; the expensive autofill (brand/currency/
  // cost) fires on real selection via onChange / onBlur → handleUpdateItem. This
  // mirrors the PO form's working SKU dropdown. (Rishabh 2026-06-26)
  const handleSkuInputChange = (index, v) => {
    markTouched(); // MUI Select/Autocomplete emit no DOM change event, so mark at the write
    setTransferForm(prev => {
      if (!prev.items[index] || prev.items[index].sku === v) return prev;
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], sku: v };
      return { ...prev, items: newItems };
    });
  };

  const handleUpdateItem = (index, field, value) => {
    markTouched(); // MUI Select/Autocomplete emit no DOM change event, so mark at the write
    // Functional update (matches the sibling writers handleSkuInputChange /
    // handleAddItem). Deriving newItems from `prev.items` — not the closure's
    // transferForm.items — is what stops rapid sibling edits from clobbering
    // each other, the front-of-house half of the backend line-scramble fix.
    setTransferForm(prev => {
      if (!prev.items[index]) return prev;
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      if (field === 'sku') {
        // Keep the stored SKU in its database-native case so downstream lookups
        // (skuSummary.find, fetchSkuMeta, batch find) match. The on-screen
        // display is uppercased separately via textTransform CSS.
        const batch = (batchOptionsBySku[value] || [])[0];
        if (batch) newItems[index].batch_number = batch.batch_number;
        // Fill the line's Description immediately from the matching catalog/stock
        // row so the operator doesn't wait on the 300ms-debounced fetchSkuMeta
        // effect. Case-insensitive match mirrors fetchSkuMeta's lookup.
        const stockOption = skuSummary.find(s => String(s.sku || '').toUpperCase() === String(value || '').toUpperCase());
        if (stockOption?.product_name) newItems[index].product_name = stockOption.product_name;
        // Reset unit_cost to 0 when SKU changes — the auto-fetch below will
        // populate it for the new SKU. Operator-typed values are only sticky
        // *within* the same SKU; changing the SKU implies wanting the new
        // SKU's cost.
        newItems[index].unit_cost = 0;
      }
      // RC5 (Rishabh round 3): keep quantity_shipped + quantity_received in
      // sync with quantity whenever the corresponding milestone date is
      // already set. Pre-fix, the auto-fill effects only fired on the date
      // *changing* — so if the operator typed Dispatch Date BEFORE the qty
      // (a natural header-first order), quantity_shipped stayed at '' and the
      // CREATE flow stored quantity_shipped=0. On reopen, Qty Dispatched
      // rendered empty even though Dispatch Date was set + status was draft.
      // Now: any qty edit propagates to qty_shipped (if dispatch_date set) and
      // qty_received (if completed_date set), regardless of input order.
      if (field === 'quantity') {
        if (prev.dispatch_date) {
          newItems[index].quantity_shipped = value;
        }
        if (prev.completed_date) {
          newItems[index].quantity_received = value;
          // Bug 2 invariant: once completed, qty lives in Transferred not Dispatched.
          newItems[index].quantity_shipped = '';
        }
      }
      return { ...prev, items: newItems };
    });
    // Auto-fill unit_cost from product_cogs (rolled-up for bundles) when the
    // SKU is picked. Backend uses upper(sku) = upper(?) for matching so case
    // doesn't matter on the wire.
    if (field === 'sku' && value) {
      // 2026-06-18 (Rishabh): auto-set Brand + Currency from the picked SKU —
      // Brand from the catalog (product_master), Currency from the SKU's most
      // recent PO (e.g. ALK_IH → Allkme + EUR, since Aurora POs are EUR). Only
      // when Brand isn't already chosen, so the FIRST SKU drives it (transfers
      // are single-brand) and a deliberate later choice isn't overridden.
      // Resolves to the currency this lookup set on the header ('' if it set none), so the
      // cost prefill below can convert into the currency the transfer actually ends up in.
      const originCurrency = Number(transferForm.brand_id) ? Promise.resolve('') :
        api.get(`/api/stock-levels/sku-origin/${encodeURIComponent(value)}`).then(r => {
          const d = r?.data?.data || {};
          let applied = '';
          setTransferForm(prev => {
            if (Number(prev.brand_id)) return prev; // set in the meantime — don't clobber
            const next = { ...prev };
            if (Number(d.brand_id)) next.brand_id = Number(d.brand_id);
            if (d.currency) { next.currency = d.currency; applied = d.currency; }
            return next;
          });
          return applied;
        }).catch(() => '');
      if (!batchOptionsBySku[value] && transferForm.from_warehouse_code) {
        const askedWarehouse = transferForm.from_warehouse_code;
        getSkuBatches(value, {
          warehouse_code: askedWarehouse,
          brand_id: transferForm.brand_id || undefined,
        }).then(r => {
          if (fromWarehouseRef.current !== askedWarehouse) return; // the previous warehouse's batches
          const batches = r?.data?.batches || r?.batches || [];
          setBatchOptionsBySku(prev => ({ ...prev, [value]: batches }));
        }).catch(() => {
          if (fromWarehouseRef.current !== askedWarehouse) return;
          setBatchOptionsBySku(prev => ({ ...prev, [value]: [] }));
        });
      }
      Promise.all([api.get('/api/cogs/sku-cost', {
        params: {
          sku: value,
          warehouse_code: transferForm.from_warehouse_code || undefined,
          brand_id: transferForm.brand_id || undefined,
        },
      }), originCurrency]).then(async ([r, appliedCurrency]) => {
        const data = r?.data?.data;
        const skuKey = `${transferForm.from_warehouse_code || ''}|${String(value).toUpperCase()}`;
        const noFactoryCost = r?.data?.reason === 'factory_no_cost';
        setFactoryNoCostSkus(prev => ({ ...prev, [skuKey]: noFactoryCost }));
        if (!data || !Number(data.unit_cost)) return;
        // The cost comes back in its own currency (product_cogs and FIFO costs are USD), but the
        // line is priced in the TRANSFER's currency and receive converts it from there to USD.
        // Copying a USD figure into a GBP/EUR transfer converted it twice (TRF-757/758/1064,
        // Rishabh 2026-11-04). Convert into the transfer currency first; a supplier PO price
        // already in that currency is used as-is. Neither currency is ever assumed: a cost whose
        // currency the API does not know (batch fallback answers '') is left for the operator
        // (the backend's own batch-cost fallback converts it on save), and a transfer with no
        // currency yet takes the cost's own currency. No rate: leave the cost for the operator.
        const fromCcy = String(data.currency || '').toUpperCase();
        if (!fromCcy) return;
        const toCcy = String(appliedCurrency || transferForm.currency || fromCcy).toUpperCase();
        let cost = Number(data.unit_cost);
        if (fromCcy !== toCcy) {
          if (String(data.po_currency || '').toUpperCase() === toCcy && Number(data.po_unit_cost) > 0) {
            cost = Number(data.po_unit_cost);
          } else {
            try {
              const fx = await api.get('/api/accounting/exchange-rates/rate', {
                params: { from: fromCcy, to: toCcy, date: transferForm.transfer_date },
              });
              const rate = Number(fx?.data?.data?.rate_numeric);
              if (!rate || !isFinite(rate)) return;
              cost = Number((cost * rate).toFixed(4));
            } catch (e) { return; }
          }
        }
        setTransferForm(prev => {
          const items = [...prev.items];
          // Only set unit_cost if the SKU on this row is still the one we
          // looked up — guards against stale responses if the operator
          // changed the SKU again while the fetch was in flight.
          if (items[index]?.sku !== value) return prev;
          // Currency changed while the lookup ran: the converted figure is in the wrong one.
          const prevCcy = String(prev.currency || '').toUpperCase();
          if (prevCcy && prevCcy !== toCcy) return prev;
          items[index] = { ...items[index], unit_cost: cost };
          return { ...prev, currency: prev.currency || toCcy, items };
        });
      }).catch(() => { /* silent — operator can type manually */ });
    }
  };

  // Currency-change confirm dialog. When the operator picks a new currency and
  // values are already entered, we offer three options: convert at the FX rate,
  // just relabel (if they typed values already in the new currency), or cancel
  // the change. Silent switch when no values are entered yet.
  const [currencyConvertDialog, setCurrencyConvertDialog] = useState({
    open: false, oldCurrency: '', newCurrency: '', rate: null, loading: false, error: null,
  });

  // Freight / Customs entered in a NON-transfer currency (Rishabh 2026-06-19):
  // freight invoices often arrive in AUD/USD/GBP regardless of the transfer's
  // currency. This helper takes a foreign amount + currency, converts it to the
  // transfer currency at the FX rate for the transfer date (same rate service the
  // transfer-currency switch uses), and writes the converted value into the standard
  // freight_cost / customs_duty field — so the landed-cost calc (which works in the
  // transfer currency) is unchanged. The original (e.g. "AUD 500 @ 0.86") is shown
  // inline; persisting it for audit is a follow-up (needs a freight_currency column).
  const [fxConvert, setFxConvert] = useState({
    freight: { amount: '', currency: '', converting: false, note: '' },
    customs: { amount: '', currency: '', converting: false, note: '' },
  });

  const applyForeignLandedCost = async (which) => {
    const field = which === 'freight' ? 'freight_cost' : 'customs_duty';
    const entry = fxConvert[which];
    const amount = Number(entry.amount) || 0;
    const fromCcy = entry.currency;
    const toCcy = transferForm.currency || 'USD';
    if (!amount || !fromCcy) return;
    if (fromCcy === toCcy) {
      // Same currency — no conversion, just drop the amount straight in.
      setTransferForm(prev => ({ ...prev, [field]: Number(amount.toFixed(2)) }));
      setFxConvert(prev => ({ ...prev, [which]: { ...prev[which], converting: false, note: '' } }));
      return;
    }
    setFxConvert(prev => ({ ...prev, [which]: { ...prev[which], converting: true, note: '' } }));
    try {
      const r = await api.get('/api/accounting/exchange-rates/rate', {
        params: { from: fromCcy, to: toCcy, date: transferForm.transfer_date },
      });
      const rate = Number(r?.data?.data?.rate_numeric);
      if (!rate || !isFinite(rate)) throw new Error('No rate returned');
      const converted = Number((amount * rate).toFixed(2));
      setTransferForm(prev => ({ ...prev, [field]: converted }));
      setFxConvert(prev => ({ ...prev, [which]: { ...prev[which], converting: false,
        note: `${fromCcy} ${amount.toFixed(2)} → ${toCcy} ${converted.toFixed(2)} @ ${rate.toFixed(4)}` } }));
    } catch (e) {
      setFxConvert(prev => ({ ...prev, [which]: { ...prev[which], converting: false, note: 'FX rate unavailable — enter the amount in the transfer currency directly' } }));
    }
  };

  const handleCurrencyChange = async (newCurrency) => {
    markTouched(); // MUI Select/Autocomplete emit no DOM change event, so mark at the write
    const oldCurrency = transferForm.currency || 'USD';
    if (newCurrency === oldCurrency) return;
    const hasValues = transferForm.items.some(it => Number(it.unit_cost) > 0)
      || Number(transferForm.freight_cost) > 0
      || Number(transferForm.customs_duty) > 0;
    if (!hasValues) {
      setTransferForm(prev => ({ ...prev, currency: newCurrency }));
      return;
    }
    setCurrencyConvertDialog({ open: true, oldCurrency, newCurrency, rate: null, loading: true, error: null });
    try {
      const r = await api.get('/api/accounting/exchange-rates/rate', {
        params: { from: oldCurrency, to: newCurrency, date: transferForm.transfer_date },
      });
      const rate = Number(r?.data?.data?.rate_numeric);
      if (!rate || !isFinite(rate)) throw new Error('No rate returned');
      setCurrencyConvertDialog(prev => ({ ...prev, rate, loading: false }));
    } catch (e) {
      setCurrencyConvertDialog(prev => ({ ...prev, loading: false, error: 'FX rate unavailable' }));
    }
  };

  const applyCurrencyConvert = (mode) => {
    const { newCurrency, rate } = currencyConvertDialog;
    if (mode === 'convert' && rate) {
      setTransferForm(prev => ({
        ...prev,
        currency: newCurrency,
        freight_cost: Number((Number(prev.freight_cost || 0) * rate).toFixed(4)) || 0,
        customs_duty: Number((Number(prev.customs_duty || 0) * rate).toFixed(4)) || 0,
        items: prev.items.map(it => ({
          ...it,
          unit_cost: Number((Number(it.unit_cost || 0) * rate).toFixed(4)) || 0,
        })),
      }));
    } else if (mode === 'relabel') {
      setTransferForm(prev => ({ ...prev, currency: newCurrency }));
    }
    setCurrencyConvertDialog({ open: false, oldCurrency: '', newCurrency: '', rate: null, loading: false, error: null });
  };

  // Open the shortage-confirm dialog for the short lines and async-load the
  // "why" breakdown per SKU: on-hand + reserved come from the batches we've
  // already loaded for this warehouse; incoming (open POs + in-transit) is
  // fetched from the stock-levels incoming endpoint. Degrades gracefully — if
  // the incoming call fails the dialog still shows need/available/short.
  const openShortageDialog = (problemLines, mode) => {
    const lines = problemLines.map(l => {
      // Only the short lines need the on-hand/reserved/incoming breakdown.
      if (!l.isShort) return { ...l, onHand: 0, reserved: 0, incoming: null, incomingItems: [], incomingLoading: false };
      const batches = batchOptionsBySku[l.sku] || batchOptionsBySku[String(l.sku).toUpperCase()] || [];
      const onHand = batches.reduce((s, b) => s + (Number(b.current_quantity) || 0), 0);
      const reserved = batches.reduce((s, b) => s + (Number(b.reserved_quantity) || 0), 0);
      return { ...l, onHand, reserved, incoming: null, incomingItems: [], incomingLoading: true };
    });
    setShortageDialog({ open: true, lines, pendingMode: mode });
    lines.forEach((l, i) => {
      if (!l.isShort) return;  // missing-cost-only lines: no incoming lookup
      api.get(`/api/stock-levels/sku/${encodeURIComponent(l.sku)}/incoming`, {
        params: { brand_id: transferForm.brand_id },
      }).then(r => {
        const data = r?.data?.data ?? r?.data ?? {};
        const items = Array.isArray(data) ? data : (data.incoming || data.items || []);
        const totalIncoming = items.reduce((s, x) => s + (Number(x.quantity ?? x.incoming_quantity ?? x.qty ?? 0) || 0), 0);
        setShortageDialog(prev => {
          if (!prev.open) return prev;
          const next = [...prev.lines];
          if (next[i]) next[i] = { ...next[i], incoming: totalIncoming, incomingItems: items.slice(0, 4), incomingLoading: false };
          return { ...prev, lines: next };
        });
      }).catch(() => {
        setShortageDialog(prev => {
          if (!prev.open) return prev;
          const next = [...prev.lines];
          if (next[i]) next[i] = { ...next[i], incomingLoading: false };
          return { ...prev, lines: next };
        });
      });
    });
  };

  // mode='back' navigates to the list after a brief success toast; 'stay'
  // keeps the operator on the edit page so they can keep iterating (add more
  // line items, fix logistics, etc.). 'Create Transfer' always uses 'back'
  // since there's no edit-mode page to stay on yet.
  const handleSave = async (mode = 'back', bypassShortage = false) => {
    if (!transferForm.from_warehouse_code || !transferForm.to_warehouse_code) {
      setError('Pick both source and destination warehouses.'); return;
    }
    if (transferForm.from_warehouse_code === transferForm.to_warehouse_code) {
      setError('Source and destination warehouse cannot be the same.'); return;
    }
    if (transferForm.items.some(i => !i.sku || !i.quantity)) {
      setError('Every line item needs a SKU and quantity.'); return;
    }
    // Pre-ship guard (Rishabh 2026-06-19). When this Save would SHIP or COMPLETE
    // the transfer (stock leaves the source — dispatch_date OR completed_date set),
    // block + confirm if any line is (a) short on available stock, or (b) missing a
    // unit cost (would post with blank/zero cost). Pure drafts (neither date) skip
    // it. The short check only flags when availability data is loaded (skuSummary
    // non-empty) so an un-loaded picker never false-positives; the missing-cost
    // check is independent of stock data.
    const willShipNow = (isEdit && (transferForm.dispatch_date || transferForm.completed_date) && (existingStatus === 'draft' || !existingStatus))
      || (!isEdit && (!!transferForm.dispatch_date || !!transferForm.completed_date));
    if (willShipNow && !bypassShortage) {
      const haveStockData = skuSummary.length > 0;
      const problemLines = transferForm.items
        .filter(it => it.sku && Number(it.quantity) > 0)
        .map(it => {
          const key = String(it.sku || '').toUpperCase();
          const opt = skuSummary.find(s => String(s.sku || '').toUpperCase() === key);
          const available = Number(opt?.total_available) || 0;
          const requested = Number(it.quantity) || 0;
          const isShort = haveStockData && (requested - available) > 0;
          const missingCost = !(Number(it.unit_cost) > 0);
          return { sku: it.sku, requested, available, short: Math.max(0, requested - available), isShort, missingCost };
        })
        .filter(l => l.isShort || l.missingCost);
      if (problemLines.length > 0) {
        openShortageDialog(problemLines, mode);
        return;
      }
    }
    setSaving(true); setError(null); setWarning(null);
    // Receive responses of this save (cost_rows_skipped) — shown here, and carried to the detail page on navigate.
    const receiveResponses = [];
    let createdTransferId = null; // set on the create path so we can open the new transfer's detail
    // Pre-compute the step list so the operator sees the upcoming sequence
    // before each step runs. Steps mark themselves running/done/error as
    // they execute. Helper lambdas keep the handler readable.
    const willShip = isEdit && transferForm.dispatch_date && (existingStatus === 'draft' || !existingStatus);
    const willReceive = isEdit && transferForm.completed_date &&
      (existingStatus === 'draft' || !existingStatus || existingStatus === 'in_transit');
    // RC4 (Rishabh round 2): operator-driven reverse transitions. Clearing
    // Completed Date on a completed transfer must revert to in_transit
    // (reverse-receive: deletes destination batches + transfer_in tx).
    // Clearing Dispatch Date on an in_transit transfer must revert to draft
    // (reverse-ship: restores source-batch qty + deletes transfer_out tx).
    // Order matters when BOTH are cleared on a completed transfer: receive
    // reverse first (completed→in_transit), then ship reverse (in_transit→draft).
    const willReverseReceive = isEdit && !transferForm.completed_date && existingStatus === 'completed';
    const willReverseShip    = isEdit && !transferForm.dispatch_date &&
      (existingStatus === 'in_transit' || willReverseReceive);
    const initialSteps = isEdit
      ? [
          { key: 'update', label: 'Updating header & items', status: 'pending', error: null },
          ...(willReverseReceive ? [{ key: 'reverse-receive', label: 'Un-receiving stock', status: 'pending', error: null }] : []),
          ...(willReverseShip    ? [{ key: 'reverse-ship',    label: 'Un-dispatching',     status: 'pending', error: null }] : []),
          ...(willShip ? [{ key: 'ship', label: 'Marking dispatched', status: 'pending', error: null }] : []),
          ...(willReceive ? [{ key: 'receive', label: 'Receiving stock at destination', status: 'pending', error: null }] : []),
        ]
      : [{ key: 'create', label: 'Creating transfer', status: 'pending', error: null }];
    setSaveSteps(initialSteps);
    const markStep = (key, status, errMsg = null) => {
      setSaveSteps(prev => prev.map(s => s.key === key ? { ...s, status, error: errMsg } : s));
    };
    try {
      if (isEdit) {
        markStep('update', 'running');
        // Backend updateWarehouseTransfer only persists items + from/to + transfer_date + notes today.
        // Sending the full form is harmless (extra fields are ignored), but be explicit so the
        // payload is auditable. See backend/warehouse-transfers/service.js:1061.
        await updateTransfer(routeId, {
          from_warehouse_code: transferForm.from_warehouse_code,
          to_warehouse_code: transferForm.to_warehouse_code,
          transfer_date: transferForm.transfer_date,
          notes: transferForm.notes,
          // Logistics fields — persisted via the always-on logistics path on
          // the backend regardless of status (carrier change post-dispatch
          // shouldn't require reverting to draft).
          carrier: transferForm.carrier,
          tracking_number: transferForm.tracking_number,
          po_number: transferForm.po_number || '',
          drive_link: transferForm.drive_link || '',
          transport_mode: transferForm.transport_mode,
          shipping_method: transferForm.shipping_method,
          incoterms: transferForm.incoterms || '',
          dispatch_date: transferForm.dispatch_date || '',
          expected_arrival_date: transferForm.expected_arrival_date || '',
          brand_id: Number(transferForm.brand_id) || 0,
          currency: transferForm.currency,
          from_subsidiary_id: Number(transferForm.from_subsidiary_id) || 0,
          to_subsidiary_id: Number(transferForm.to_subsidiary_id) || 0,
          // RC2 (Rishabh round 2): include freight/customs + packing-list +
          // landed-cost-method + customer_notes so they persist across save
          // (logistics path on the backend accepts them since 2026-05-20).
          freight_cost: parseFloat(transferForm.freight_cost) || 0,
          customs_duty: parseFloat(transferForm.customs_duty) || 0,
          landed_cost_method: transferForm.landed_cost_method || 'by_value',
          customer_notes: transferForm.customer_notes || '',
          description_of_goods: transferForm.description_of_goods || '',
          cartons: parseInt(transferForm.cartons) || 0,
          per_case: parseInt(transferForm.per_case) || 0,
          gross_weight_kg: parseFloat(transferForm.gross_weight_kg) || 0,
          net_weight_kg: parseFloat(transferForm.net_weight_kg) || 0,
          // Whole-transfer pallet details (Rishabh 2026-09-16).
          pallet_count: parseInt(transferForm.pallet_count, 10) || 0,
          pallet_dimensions: transferForm.pallet_dimensions || '',
          pallet_weight_kg: parseFloat(transferForm.pallet_weight_kg) || 0,
          // Consignee + shipper overrides (5 each). Empty = clear override.
          override_consignee_name: transferForm.override_consignee_name,
          override_consignee_address_line1: transferForm.override_consignee_address_line1,
          override_consignee_city: transferForm.override_consignee_city,
          override_consignee_country: transferForm.override_consignee_country,
          override_consignee_postal_code: transferForm.override_consignee_postal_code,
          override_shipper_name: transferForm.override_shipper_name,
          override_shipper_address_line1: transferForm.override_shipper_address_line1,
          override_shipper_city: transferForm.override_shipper_city,
          override_shipper_country: transferForm.override_shipper_country,
          override_shipper_postal_code: transferForm.override_shipper_postal_code,
          override_shipper_contact_name: transferForm.override_shipper_contact_name,
          override_shipper_contact_phone: transferForm.override_shipper_contact_phone,
          override_shipper_contact_email: transferForm.override_shipper_contact_email,
          items: transferForm.items.map(i => ({
            id: i.id,  // present on existing rows, undefined on newly added rows
            sku: i.sku,
            batch_number: i.batch_number,
            quantity: Number(i.quantity) || 0,
            // Per-item editable fields — backend persists these via ALTER UPDATE
            // on warehouse_transfer_items regardless of transfer status. Pre-fix
            // these were never sent, so operator edits to unit_cost / hs_code /
            // etc. silently reverted on save (Rishabh: TRF-79848 + in-transit
            // transfers).
            unit_cost: Number(i.unit_cost) || 0,
            discount_percent: Number(i.discount_percent) || 0,
            tax_percent: Number(i.tax_percent) || 0,
            hs_code: i.hs_code || '',
            country_of_origin: i.country_of_origin || '',
            description: i.description || '',
            per_case: Number(i.per_case) || 0,
            // Per-SKU-line packing details (Rishabh 2026-09-16).
            cartons: parseInt(i.cartons, 10) || 0,
            net_weight_kg: Number(i.net_weight_kg) || 0,
            gross_weight_kg: Number(i.gross_weight_kg) || 0,
            notes: i.notes || '',
          })),
        });
        markStep('update', 'done');
        // RC4: reverse transitions first (receive before ship — order matters
        // when both dates are cleared on a completed transfer).
        if (willReverseReceive) {
          markStep('reverse-receive', 'running');
          try {
            await reverseReceiveTransfer(routeId);
            setExistingStatus('in_transit');
            markStep('reverse-receive', 'done');
          } catch (e) {
            const msg = extractApiError(e);
            console.warn('Reverse-receive failed:', msg);
            markStep('reverse-receive', 'error', msg);
          }
        }
        if (willReverseShip) {
          markStep('reverse-ship', 'running');
          try {
            await reverseShipTransfer(routeId);
            setExistingStatus('draft');
            markStep('reverse-ship', 'done');
          } catch (e) {
            const msg = extractApiError(e);
            console.warn('Reverse-ship failed:', msg);
            markStep('reverse-ship', 'error', msg);
          }
        }
        // Status-transition step. Setting Dispatch Date on a draft transfer
        // implies "the transfer is now in-transit" — call the /ship endpoint
        // so backend creates the source-side inventory transactions and flips
        // status='in_transit'. Similarly Completed Date → /receive (creates
        // destination batches, flips status='completed'). These calls are
        // idempotent against current status, so re-saving a completed
        // transfer with the same dates is a no-op.
        if (willShip) {
          markStep('ship', 'running');
          try {
            await shipTransfer(routeId, {
              shipped_date: transferForm.dispatch_date,
              items: transferForm.items
                .filter(i => i.id)
                .map(i => ({ item_id: i.id, quantity_shipped: Number(i.quantity_shipped) || Number(i.quantity) || 0 })),
            });
            setExistingStatus('in_transit');
            markStep('ship', 'done');
          } catch (shipErr) {
            const msg = extractApiError(shipErr);
            console.warn('Auto-ship after save failed:', msg);
            markStep('ship', 'error', msg);
          }
        }
        if (willReceive) {
          markStep('receive', 'running');
          try {
            receiveResponses.push(await receiveTransfer(routeId, {
              received_date: transferForm.completed_date,
              items: transferForm.items
                .filter(i => i.id)
                // An entered quantity wins, 0 included (a line lost in transit): `||` turned an entered 0
                // into the full shipped qty. Blank still falls back to shipped, as before.
                .map(i => ({ item_id: i.id, quantity_received: (i.quantity_received !== '' && i.quantity_received != null)
                  ? (Number(i.quantity_received) || 0)
                  : (Number(i.quantity_shipped) || Number(i.quantity) || 0) })),
            }));
            setExistingStatus('completed');
            markStep('receive', 'done');
          } catch (recvErr) {
            const msg = extractApiError(recvErr);
            console.warn('Auto-receive after save failed:', msg);
            markStep('receive', 'error', msg);
          }
        }
        setSuccess('Transfer updated');
      } else {
        markStep('create', 'running');
        // RC5: belt-and-suspenders for the create flow — if the operator's
        // data-entry order missed the auto-fill effect (e.g. set Dispatch
        // Date BEFORE typing qty), backfill quantity_shipped from quantity
        // at submit time. handleUpdateItem now keeps these in sync going
        // forward, this guards already-stale state from before that fix.
        const normalizedItems = transferForm.items.map(it => ({
          ...it,
          // Coerce numerics to Numbers — the form stores quantity/etc. as
          // strings, and `...it` would otherwise ship them as strings on CREATE
          // (the UPDATE payload already Number()-coerces). The backend summed
          // string quantities with `+=`, concatenating them into a giant value
          // that overflowed the Decimal column (Rishabh, SUZH->COSTEC,
          // 2026-06-09). Backend now coerces too; this is belt-and-suspenders.
          quantity: Number(it.quantity) || 0,
          unit_cost: Number(it.unit_cost) || 0,
          per_case: Number(it.per_case) || 0,
          discount_percent: Number(it.discount_percent) || 0,
          tax_percent: Number(it.tax_percent) || 0,
          quantity_shipped: transferForm.dispatch_date ? (Number(it.quantity_shipped) || Number(it.quantity) || 0) : (Number(it.quantity_shipped) || 0),
          quantity_received: transferForm.completed_date ? (Number(it.quantity_received) || Number(it.quantity_shipped) || Number(it.quantity) || 0) : (Number(it.quantity_received) || 0),
        }));
        const payload = {
          ...transferForm,
          items: normalizedItems,
          cartons: parseInt(transferForm.cartons) || 0,
          per_case: parseInt(transferForm.per_case) || 0,
          gross_weight_kg: parseFloat(transferForm.gross_weight_kg) || 0,
          net_weight_kg: parseFloat(transferForm.net_weight_kg) || 0,
          // Whole-transfer pallet details (Rishabh 2026-09-16).
          pallet_count: parseInt(transferForm.pallet_count, 10) || 0,
          pallet_dimensions: transferForm.pallet_dimensions || '',
          pallet_weight_kg: parseFloat(transferForm.pallet_weight_kg) || 0,
        };
        const createResp = await createTransfer(payload);
        markStep('create', 'done');
        // RC5: auto-ship + auto-receive on create flow — mirrors the EDIT
        // branch's behavior so a transfer created WITH a Dispatch Date
        // transitions to in_transit immediately (was previously stranded as
        // a draft with dispatch_date set, which left Qty Dispatched=0 in DB
        // for source_batch-linked items and Status=Draft despite the date).
        const newId = createResp?.transfer?.id || createResp?.data?.id;
        createdTransferId = newId;
        // A transfer with a Completed Date implies it was also dispatched — ship THEN
        // receive. Previously the auto-receive was nested inside `if (dispatch_date)`,
        // so entering ONLY the Completed Date on creation (no Dispatch Date) skipped
        // BOTH transitions and stranded the transfer (Rishabh 2026-06-22: status
        // inconsistently stayed In Transit, needing a reopen + re-save to complete —
        // which worked only because the edit path's willReceive then fired). Now: ship
        // if EITHER date is set (falling back to the completed date as the dispatch
        // date for backfilled transfers), then receive whenever a Completed Date is set.
        if (newId && (transferForm.dispatch_date || transferForm.completed_date)) {
          try {
            await shipTransfer(newId, {
              shipped_date: transferForm.dispatch_date || transferForm.completed_date,
              items: (createResp?.transfer?.items || []).map(i => ({
                item_id: i.id,
                quantity_shipped: Number(i.quantity_shipped) || Number(i.quantity_requested) || 0,
              })),
            });
          } catch (shipErr) {
            console.warn('Auto-ship after create failed:', extractApiError(shipErr));
          }
          if (transferForm.completed_date) {
            try {
              // Re-fetch the just-shipped transfer to get CURRENT item ids + shipped
              // qty. Auto-ship can rotate item ids (backend delete+insert pattern), so
              // the createResp ids can be stale — receiving with stale ids credits 0
              // and strands the transfer In-Transit with received=0 (Rishabh 2026-07-01,
              // TRF-777). Fresh ids (or none — backend defaults an unspecified line to
              // full-received) let it complete.
              let recvItems = [];
              try {
                const fresh = await getTransfer(newId);
                const ft = fresh?.transfer || fresh?.data;
                recvItems = (ft?.items || []).map(i => ({
                  item_id: i.id,
                  quantity_received: Number(i.quantity_shipped) || Number(i.quantity_requested) || 0,
                }));
              } catch (_) { /* fall back to full-receive default (empty items) */ }
              receiveResponses.push(await receiveTransfer(newId, {
                received_date: transferForm.completed_date,
                items: recvItems,
              }));
            } catch (recvErr) {
              // Surface it — do NOT swallow. A silently-failed receive leaves the
              // transfer looking done (arrival date stamped) but stuck In-Transit.
              const msg = extractApiError(recvErr);
              setError(`Transfer created & dispatched, but auto-receive failed: ${msg}. Open the transfer and set the Completed Date to finish receiving.`);
            }
          }
        }
        setSuccess('Transfer created');
      }
      const costWarning = skippedCostRowsWarning(skippedCostRows(receiveResponses), 'Transfer received');
      setWarning(costWarning);
      // Everything on screen is now persisted, so leaving is safe again.
      setUserTouched(false);
      if (mode === 'stay' && isEdit) {
        // Refetch the transfer to pick up fresh per-line ids + server-side
        // values (status, dispatch/completed dates, qty_shipped/received).
        // Without this, the backend's ELSE branch in updateWarehouseTransfer
        // rotates item ids on every save (delete+insert pattern, MergeTree
        // can't UPDATE). The stale ids in transferForm.items would cause the
        // NEXT save to trip the structuralChange detector → reverseShip →
        // status reverts to draft. Bug 1 (Rishabh, "details disappear on
        // re-edit") was caused by this.
        try {
          const tRes = await getTransfer(routeId);
          const t = tRes?.transfer || tRes?.data;
          if (t) {
            setExistingStatus(t.status);
            const loadedDispatch2 = (t.dispatch_date || '').toString().slice(0, 10);
            const loadedCompleted2 = (t.actual_arrival_date || '').toString().slice(0, 10);
            // Gate on status, not date — partial receives stamp the date but stay in_transit.
            const isCompletedLoad2 = t.status === 'completed';
            setTransferForm(prev => ({
              ...prev,
              dispatch_date: (loadedDispatch2 && loadedDispatch2 !== '1970-01-01') ? loadedDispatch2 : '',
              completed_date: isCompletedLoad2 ? loadedCompleted2 : '',
              items: (t.items || []).map(i => ({
                id: i.id,
                // MUST mirror the initial-load mapping (~line 455). source_batch_id is
                // required by groupTransferLines (Number(sbid)<=0) — omitting it makes it
                // undefined -> NaN<=0 -> false -> every row ungroups ("duplicate lines"
                // reappear after save). The packing fields must also carry over or they
                // vanish from the form after save even though they persisted (Rishabh 2026-09-16).
                source_batch_id: Number(i.source_batch_id || 0),
                sku: i.sku || '',
                batch_number: i.batch_number || '',
                quantity: Number(i.quantity_requested ?? i.quantity ?? 0) || '',
                quantity_shipped: isCompletedLoad2 ? '' : (Number(i.quantity_shipped || 0) || ''),
                quantity_received: Number(i.quantity_received || 0) || '',
                discrepancy_qty: Number(i.discrepancy_qty || 0) || 0,
                discrepancy_reason: i.discrepancy_reason || '',
                hs_code: i.hs_code || '',
                country_of_origin: i.country_of_origin || '',
                unit_cost: Number(i.unit_cost || 0),
                discount_percent: Number(i.discount_percent || 0),
                tax_percent: Number(i.tax_percent || 0),
                cartons: Number(i.cartons || 0) || '',
                per_case: Number(i.per_case || 0) || '',
                net_weight_kg: Number(i.net_weight_kg || 0) || '',
                gross_weight_kg: Number(i.gross_weight_kg || 0) || '',
                product_name: i.product_name || '',
              })),
            }));
            // Keep the auto-fill guard refs in lockstep with the form so the
            // next user-driven date change still triggers the auto-fill, but
            // the post-save re-render doesn't.
            prevDispatchDateRef.current = (loadedDispatch2 && loadedDispatch2 !== '1970-01-01') ? loadedDispatch2 : '';
            prevCompletedDateRef.current = isCompletedLoad2 ? loadedCompleted2 : '';
          }
        } catch (_) { /* silent — operator can manually reload */ }
        // Refetch source-warehouse stock so the per-line Available indicator
        // + dropdown badges reflect any reverse-ship / reverse-receive that
        // just ran. Without this the operator sees the old (pre-reversal)
        // values and the indicator stays at "Out of Stock" even though stock
        // has actually been restored in the source warehouse.
        if (fetchAvailableStock.current) {
          try { await fetchAvailableStock.current(); } catch (_) { /* silent */ }
        }
        setTimeout(() => setSuccess(null), 2500);
      } else {
        // After a create OR an edit-save (Save & Close), open the transfer's DETAIL
        // (not the full list) so the operator can verify its ID + status without
        // hunting via filters — createdTransferId on create, routeId on edit. Falls
        // back to the list only if neither is available. (Rishabh 2026-07-01)
        const goToId = createdTransferId || routeId;
        setTimeout(() => navigate(
          goToId ? `/inventory/transfers/${goToId}` : '/inventory/transfers',
          costWarning ? { state: { costWarning } } : undefined
        ), 600);
      }
    } catch (e) {
      const msg = extractApiError(e, 'Failed to save');
      setError(msg);
      // Mark whatever step was running as failed so the inline indicator
      // doesn't leave a spinner stuck on. Any step still 'pending' is left
      // alone (didn't start) and rendered as inactive in the indicator.
      setSaveSteps(prev => prev.map(s => s.status === 'running' ? { ...s, status: 'error', error: msg } : s));
    } finally {
      setSaving(false);
    }
  };

  // P4.3: Inline single-click status-transition CTAs. Equivalent to setting
  // the Dispatch / Completed date in the form and clicking Save — but the
  // operator doesn't have to scroll down and pick a date. Stamps `today` and
  // refetches so the stepper + items reflect the new state.
  const [markBusy, setMarkBusy] = useState(false);
  const markDispatched = async () => {
    if (markBusy || saving) return;
    setMarkBusy(true); setError(null);
    try {
      const today = new Date().toISOString().split('T')[0];
      await shipTransfer(routeId, {
        shipped_date: today,
        items: transferForm.items
          .filter(i => i.id)
          .map(i => ({ item_id: i.id, quantity_shipped: Number(i.quantity_shipped) || Number(i.quantity) || 0 })),
      });
      setExistingStatus('in_transit');
      // Pull fresh state so dispatch_date appears in the form and items
      // reflect server-set qty_shipped.
      const tRes = await getTransfer(routeId);
      const t = tRes?.transfer || tRes?.data;
      if (t) {
        setTransferForm(prev => ({ ...prev, dispatch_date: (t.dispatch_date || today).toString().slice(0, 10) }));
      }
      setSuccess('Marked dispatched');
      setTimeout(() => setSuccess(null), 2500);
    } catch (e) {
      setError(extractApiError(e, 'Mark dispatched failed'));
    } finally {
      setMarkBusy(false);
    }
  };
  const markReceived = async () => {
    if (markBusy || saving) return;
    setMarkBusy(true); setError(null); setWarning(null);
    try {
      const today = new Date().toISOString().split('T')[0];
      const received = await receiveTransfer(routeId, {
        received_date: today,
        items: transferForm.items
          .filter(i => i.id)
          .map(i => ({ item_id: i.id, quantity_received: Number(i.quantity_received) || Number(i.quantity_shipped) || Number(i.quantity) || 0 })),
      });
      setExistingStatus('completed');
      const tRes = await getTransfer(routeId);
      const t = tRes?.transfer || tRes?.data;
      if (t) {
        setTransferForm(prev => ({
          ...prev,
          completed_date: (t.actual_arrival_date || today).toString().slice(0, 10),
          items: prev.items.map(i => ({ ...i, quantity_shipped: '' })),
        }));
      }
      setSuccess('Marked received');
      setWarning(skippedCostRowsWarning(skippedCostRows(received), 'Transfer received'));
      setTimeout(() => setSuccess(null), 2500);
    } catch (e) {
      setError(extractApiError(e, 'Mark received failed'));
    } finally {
      setMarkBusy(false);
    }
  };
  // D6 (2026-07-17): explicit reverse-transition CTAs next to the stepper, mirroring
  // Mark Dispatched / Mark Received. Previously the only way to revert was to hand-clear
  // the (segmented) date input and Save — genuinely fiddly. These call the reverse-ship /
  // reverse-receive routes directly and blank the corresponding date; the date useEffects
  // then reflow the qty columns (they don't re-hit the API, so there's no double-reverse).
  const unDispatch = async () => {
    if (markBusy || saving) return;
    setMarkBusy(true); setError(null);
    try {
      await reverseShipTransfer(routeId);
      setExistingStatus('draft');
      setTransferForm(prev => ({ ...prev, dispatch_date: '', completed_date: '' }));
      setSuccess('Un-dispatched — reverted to Draft');
      setTimeout(() => setSuccess(null), 2500);
    } catch (e) {
      setError(extractApiError(e, 'Un-dispatch failed'));
    } finally {
      setMarkBusy(false);
    }
  };
  const unReceive = async () => {
    if (markBusy || saving) return;
    setMarkBusy(true); setError(null);
    try {
      await reverseReceiveTransfer(routeId);
      setExistingStatus('in_transit');
      setTransferForm(prev => ({ ...prev, completed_date: '' }));
      setSuccess('Un-received — reverted to In Transit');
      setTimeout(() => setSuccess(null), 2500);
    } catch (e) {
      setError(extractApiError(e, 'Un-receive failed'));
    } finally {
      setMarkBusy(false);
    }
  };

  // P4.2: Cmd/Ctrl+Enter saves and stays; Cmd/Ctrl+Shift+Enter saves and
  // closes. Keep the latest handleSave reference in a ref so the document-
  // level listener doesn't close over a stale version each render.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (saving) return;
        handleSaveRef.current(e.shiftKey ? 'back' : (isEdit ? 'stay' : 'back'));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isEdit, saving]);

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>Loading…</Box>;
  }

  return (
    // onChangeCapture powers the unsaved-changes guard — see the note by
    // userTouched above for why this beats a snapshot compare here.
    <Box sx={{ p: 2, pb: 12 }} onChangeCapture={markTouched}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => guardedLeave(() => navigate(listBackTo))}><ArrowBackIcon /></IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {isEdit ? `Edit Transfer ${transferForm.transfer_number || ('#' + routeId)}` : 'New Warehouse Transfer'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {transferForm.from_warehouse_code && transferForm.to_warehouse_code
                ? `${transferForm.from_warehouse_code} → ${transferForm.to_warehouse_code}`
                : 'Pick source + destination warehouses to begin'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* P3.1: inline save progress — shown during the save sequence AND for a
          moment after, so the operator can see which sub-step failed. Dismisses
          when a new save starts or after success+stay refetch completes. */}
      {saveSteps.length > 0 && (saving || saveSteps.some(s => s.status === 'error')) && (
        <Card variant="outlined" sx={{ mb: 2, p: 1.25, bgcolor: 'background.default' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {saveSteps.map(step => {
              const colorMap = {
                pending: 'text.disabled',
                running: 'primary.main',
                done: 'success.dark',
                error: 'error.main',
              };
              const iconMap = {
                pending: '○',
                running: '⋯',
                done: '✓',
                error: '✗',
              };
              return (
                <Box key={step.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ width: 16, textAlign: 'center', color: colorMap[step.status], fontWeight: 700, fontSize: 14, lineHeight: 1 }}>
                    {iconMap[step.status]}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colorMap[step.status], fontWeight: step.status === 'running' ? 600 : 400 }}>
                    {step.label}
                    {step.status === 'running' && '…'}
                  </Typography>
                  {step.error && (
                    <Typography variant="caption" sx={{ color: 'error.main', ml: 1 }}>
                      — {step.error}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Card>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {warning && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>{warning}</Alert>}

      {isEdit && existingStatus && (
        (() => {
          // Inline status stepper — mirrors the detail-view timeline so the
          // operator sees workflow state at a glance instead of a 4-line
          // Alert banner. Cancelled gets a distinct compact pill.
          if (existingStatus === 'cancelled') {
            return (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip label="Cancelled" size="small" color="error" sx={{ fontWeight: 600 }} />
                <Typography variant="caption" color="text.secondary">
                  This transfer was cancelled. Edits will re-activate it as Open.
                </Typography>
              </Box>
            );
          }
          // DISPLAY-ONLY relabel: DB status 'draft' shows as "Open" to operators
          // (key stays 'draft' — all status logic below keys off it unchanged).
          const steps = [
            { key: 'draft', label: 'Open' },
            { key: 'in_transit', label: 'In Transit' },
            { key: 'completed', label: 'Completed' },
          ];
          const order = { draft: 0, in_transit: 1, completed: 2 };
          const currentIdx = order[existingStatus] ?? 0;
          return (
            <Card variant="outlined" sx={{ mb: 2, p: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {steps.map((s, i) => {
                  const done = i < currentIdx;
                  const active = i === currentIdx;
                  return (
                    <React.Fragment key={s.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 0.5 }}>
                        <Box sx={{
                          width: 22, height: 22, borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, lineHeight: 1,
                          bgcolor: done ? 'success.main' : (active ? 'primary.main' : 'grey.300'),
                          color: done || active ? 'common.white' : 'text.disabled',
                        }}>
                          {done ? '✓' : i + 1}
                        </Box>
                        <Typography variant="body2" sx={{
                          fontWeight: active ? 700 : 500,
                          color: done ? 'success.dark' : (active ? 'primary.dark' : 'text.disabled'),
                        }}>
                          {s.label}
                        </Typography>
                      </Box>
                      {i < steps.length - 1 && (
                        <Box sx={{
                          flex: 1, height: 2,
                          bgcolor: i < currentIdx ? 'success.light' : 'grey.300',
                        }} />
                      )}
                    </React.Fragment>
                  );
                })}
                {existingStatus !== 'draft' && (
                  <Tooltip arrow title="Logistics fields (carrier, tracking, dates, freight, packing, shipper and consignee) save in place at any stage. Lines, warehouses and the transfer date are locked until you reopen the transfer: clear the Dispatch Date and Completed Date, then Save.">
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontSize: 10, cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}>
                      what can I change?
                    </Typography>
                  </Tooltip>
                )}
              </Box>
              {linesLocked && (
                <Alert severity="info" sx={{ mt: 1.25, py: 0.25 }}>
                  Lines are locked while this transfer is {existingStatus === 'completed' ? 'completed' : 'in transit'}: the stock has moved.
                  To change lines, quantities or warehouses, clear the {existingStatus === 'completed' ? 'Completed Date and Dispatch Date' : 'Dispatch Date'} and click Save to
                  reopen it as Open (the stock is moved back), then edit and save again.
                </Alert>
              )}
              {/* P4.3: single-click transition CTAs next to the stepper.
                  Draft → "Mark Dispatched" (calls /ship). In Transit →
                  "Mark Received" (calls /receive). Both stamp today and
                  refetch. Hidden once status === 'completed'. */}
              {(existingStatus === 'draft' || existingStatus === 'in_transit' || existingStatus === 'completed') && (
                <Box sx={{ mt: 1.25, display: 'flex', gap: 1, justifyContent: 'flex-end', borderTop: '1px dashed', borderColor: 'divider', pt: 1 }}>
                  {/* Reverse (backward) transitions on the left; forward on the right (D6) */}
                  {existingStatus === 'in_transit' && (
                    <Button size="small" variant="text" color="warning" disabled={markBusy || saving} onClick={unDispatch}>
                      {markBusy ? 'Working…' : '↩ Un-dispatch'}
                    </Button>
                  )}
                  {existingStatus === 'completed' && (
                    <Button size="small" variant="text" color="warning" disabled={markBusy || saving} onClick={unReceive}>
                      {markBusy ? 'Working…' : '↩ Un-receive'}
                    </Button>
                  )}
                  {existingStatus === 'draft' && (
                    <Button size="small" variant="outlined" disabled={markBusy || saving} onClick={markDispatched}>
                      {markBusy ? 'Marking…' : '→ Mark Dispatched'}
                    </Button>
                  )}
                  {existingStatus === 'in_transit' && (
                    <Button size="small" variant="outlined" color="success" disabled={markBusy || saving} onClick={markReceived}>
                      {markBusy ? 'Marking…' : '✓ Mark Received'}
                    </Button>
                  )}
                </Box>
              )}
            </Card>
          );
        })()
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          {/* Section 1: From/To Warehouse — Autocomplete so the field is
              type-ahead searchable (a 50-warehouse Select dropdown felt
              "locked" to operators) and shows the picked warehouse's address
              underneath each picker so it's clear which physical site is on
              each side of the transfer. */}
          <Grid container spacing={2}>
            <Grid item xs={5}>
              <Autocomplete
                fullWidth
                options={warehouseOptions}
                getOptionLabel={(w) => (w ? `${w.name} (${w.code})` : '')}
                filterOptions={warehouseFilter}
                isOptionEqualToValue={(a, b) => a?.code === b?.code}
                value={warehouseOptions.find(w => w.code === transferForm.from_warehouse_code) || null}
                // Bug 1 (Rishabh): items used to be reset UNCONDITIONALLY on
                // any onChange — including spurious fires where the warehouse
                // didn't actually change. That silently wiped the saved item
                // list on re-edit. Only reset when the code is genuinely
                // changing TO a different warehouse (or being cleared).
                disabled={linesLocked}
                onChange={(_e, w) => {
                  const newCode = w?.code || '';
                  setTransferForm(prev => (
                    newCode === prev.from_warehouse_code
                      ? prev
                      : { ...prev, from_warehouse_code: newCode, items: [makeEmptyItem()] }
                  ));
                }}
                getOptionDisabled={(w) => w.code === transferForm.to_warehouse_code || w._archived}
                renderOption={(props, w) => (
                  <Box component="li" {...props} key={w.code} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 0.75, opacity: w._archived ? 0.55 : 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" fontWeight={600}>{w.name} <span style={{ fontFamily: 'monospace', fontWeight: 400, color: '#666', fontSize: 11 }}>({w.code})</span></Typography>
                      {w._archived && <Chip label="archived" size="small" sx={{ height: 16, fontSize: 9 }} />}
                    </Box>
                    {(w.city || w.country) && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        {[w.city, w.country].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="From Warehouse" required
                  helperText={warehouseOptions.find(w => w.code === transferForm.from_warehouse_code)?._archived ? 'Warehouse is archived — pick a current one to change' : undefined}
                  FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5, color: 'warning.dark' } }}
                />}
              />
              {(() => {
                // FROM side: inline-editable shipper. Mirrors the TO consignee
                // card. Empty fields fall back to the master warehouse address
                // (grey placeholder); typed values override for THIS transfer
                // only via the 5 override_shipper_* columns. Stays editable
                // post-dispatch — logistics path persists overrides without
                // touching inventory. Falls through warehouseOptions so an
                // archived source warehouse still shows the override card
                // (operator can type the shipper address manually).
                const w = warehouseOptions.find(x => x.code === transferForm.from_warehouse_code);
                if (!w) return null;
                return (
                  <Box sx={{ mt: 0.75 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField fullWidth size="small" label="Shipper Name"
                          value={transferForm.override_shipper_name}
                          placeholder={w.name || ''}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => setTransferForm({ ...transferForm, override_shipper_name: e.target.value })} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth size="small" label="Address"
                          value={transferForm.override_shipper_address_line1}
                          placeholder={w.address_line1 || '(none on warehouse)'}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => setTransferForm({ ...transferForm, override_shipper_address_line1: e.target.value })} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="City"
                          value={transferForm.override_shipper_city}
                          placeholder={w.city || '(none)'}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => setTransferForm({ ...transferForm, override_shipper_city: e.target.value })} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Postal Code"
                          value={transferForm.override_shipper_postal_code}
                          placeholder={w.postal_code || '(none)'}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => setTransferForm({ ...transferForm, override_shipper_postal_code: e.target.value })} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth size="small" label="Country"
                          value={transferForm.override_shipper_country}
                          placeholder={w.country || '(none)'}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => setTransferForm({ ...transferForm, override_shipper_country: e.target.value })} />
                      </Grid>
                      {/* Sender contact — the person dispatching THIS shipment. Prints
                          in the Commercial Invoice's "Shipped From" block; blank uses
                          the warehouse's own contact. */}
                      <Grid item xs={12}>
                        <TextField fullWidth size="small" label="Contact Name"
                          value={transferForm.override_shipper_contact_name}
                          placeholder={w.contact_name || 'Warehouse contact'}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => setTransferForm({ ...transferForm, override_shipper_contact_name: e.target.value })} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Contact Phone"
                          value={transferForm.override_shipper_contact_phone}
                          placeholder={w.contact_phone || 'Phone'}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => setTransferForm({ ...transferForm, override_shipper_contact_phone: e.target.value })} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Contact Email"
                          value={transferForm.override_shipper_contact_email}
                          placeholder={w.contact_email || 'Email'}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => setTransferForm({ ...transferForm, override_shipper_contact_email: e.target.value })} />
                      </Grid>
                    </Grid>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled', fontSize: 10 }}>
                      Leave blank to use the warehouse master address + contact (grey placeholder). Typed values override shipper for this transfer only, and print on the Commercial Invoice.
                    </Typography>
                  </Box>
                );
              })()}
            </Grid>
            <Grid item xs={2} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', pt: 4 }}>
              {/* P5.1: route visualization. Plain "→" gave no signal about HOW
                  the goods move. Now the arrow is flanked by the transport-
                  mode emoji and label so the operator sees "✈ Air" / "🚢 Sea"
                  / "🚛 Road" / "📦 Amazon FBA" at a glance. Falls back to a
                  neutral "—" when no transport mode is set. */}
              {(() => {
                const mode = transferForm.transport_mode || '';
                const map = {
                  air: { icon: '✈', label: 'Air' },
                  sea: { icon: '🚢', label: 'Sea' },
                  road: { icon: '🚛', label: 'Road' },
                  rail: { icon: '🚂', label: 'Rail' },
                  hand_carry: { icon: '🧳', label: 'Hand-carry' },
                  amazon_fba: { icon: '📦', label: 'FBA' },
                };
                const info = map[mode];
                return (
                  <>
                    {info ? (
                      <Typography sx={{ fontSize: 28, lineHeight: 1, mb: 0.5 }}>{info.icon}</Typography>
                    ) : (
                      <ArrowIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
                    )}
                    <Box sx={{ width: '100%', height: 2, bgcolor: 'divider', position: 'relative' }}>
                      <Box sx={{ position: 'absolute', right: -6, top: -4, color: 'text.disabled', fontSize: 14, lineHeight: 1 }}>▶</Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, mt: 0.5, textAlign: 'center' }}>
                      {info ? info.label : '— transport mode —'}
                    </Typography>
                  </>
                );
              })()}
            </Grid>
            <Grid item xs={5}>
              <Autocomplete
                fullWidth
                options={warehouseOptions}
                getOptionLabel={(w) => (w ? `${w.name} (${w.code})` : '')}
                filterOptions={warehouseFilter}
                isOptionEqualToValue={(a, b) => a?.code === b?.code}
                value={warehouseOptions.find(w => w.code === transferForm.to_warehouse_code) || null}
                disabled={linesLocked}
                onChange={(_e, w) => { markTouched(); setTransferForm({ ...transferForm, to_warehouse_code: w?.code || '' }); }}
                getOptionDisabled={(w) => w.code === transferForm.from_warehouse_code || w._archived}
                renderOption={(props, w) => (
                  <Box component="li" {...props} key={w.code} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 0.75, opacity: w._archived ? 0.55 : 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" fontWeight={600}>{w.name} <span style={{ fontFamily: 'monospace', fontWeight: 400, color: '#666', fontSize: 11 }}>({w.code})</span></Typography>
                      {w._archived && <Chip label="archived" size="small" sx={{ height: 16, fontSize: 9 }} />}
                    </Box>
                    {(w.city || w.country) && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        {[w.city, w.country].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="To Warehouse" required
                  helperText={warehouseOptions.find(w => w.code === transferForm.to_warehouse_code)?._archived ? 'Warehouse is archived — pick a current one to change' : undefined}
                  FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5, color: 'warning.dark' } }}
                />}
              />
              {(() => {
                // TO side: inline-editable consignee. Empty fields fall back
                // to the master warehouse address (shown as placeholder).
                // Non-empty values override for THIS transfer only (5 columns
                // override_consignee_* on warehouse_transfers, layered at PDF
                // render time in print-service.js).
                const w = warehouseOptions.find(x => x.code === transferForm.to_warehouse_code);
                if (!w) return null;
                return (
                  <Box sx={{ mt: 0.75 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField fullWidth size="small" label="Consignee Name"
                          value={transferForm.override_consignee_name}
                          placeholder={w.name || ''}
                          InputLabelProps={{ shrink: true }}
                          disabled={lockNonDraftFields}
                          onChange={(e) => setTransferForm({ ...transferForm, override_consignee_name: e.target.value })} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth size="small" label="Address"
                          value={transferForm.override_consignee_address_line1}
                          placeholder={w.address_line1 || '(none on warehouse)'}
                          InputLabelProps={{ shrink: true }}
                          disabled={lockNonDraftFields}
                          onChange={(e) => setTransferForm({ ...transferForm, override_consignee_address_line1: e.target.value })} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="City"
                          value={transferForm.override_consignee_city}
                          placeholder={w.city || '(none)'}
                          InputLabelProps={{ shrink: true }}
                          disabled={lockNonDraftFields}
                          onChange={(e) => setTransferForm({ ...transferForm, override_consignee_city: e.target.value })} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Postal Code"
                          value={transferForm.override_consignee_postal_code}
                          placeholder={w.postal_code || '(none)'}
                          InputLabelProps={{ shrink: true }}
                          disabled={lockNonDraftFields}
                          onChange={(e) => setTransferForm({ ...transferForm, override_consignee_postal_code: e.target.value })} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth size="small" label="Country"
                          value={transferForm.override_consignee_country}
                          placeholder={w.country || '(none)'}
                          InputLabelProps={{ shrink: true }}
                          disabled={lockNonDraftFields}
                          onChange={(e) => setTransferForm({ ...transferForm, override_consignee_country: e.target.value })} />
                      </Grid>
                    </Grid>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled', fontSize: 10 }}>
                      Leave blank to use the warehouse master address (grey placeholder). Typed values override consignee for this transfer only.
                    </Typography>
                  </Box>
                );
              })()}
            </Grid>
          </Grid>

          {isSupplierSourceTransfer && (
            <Alert severity="info" variant="outlined" sx={{ mt: 1.5, py: 0.5 }}>
              Inbound from supplier — make sure Brand and Currency below are correct.
            </Alert>
          )}

          {/* Sections 2-5: split with overline sub-headings so the 12+ fields
              read as 3 grouped concerns (Commercial / Logistics / Invoicing)
              rather than a flat wall of inputs. */}
          <Box sx={{ mt: 2.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="overline" sx={{ fontWeight: 600, letterSpacing: 1, color: 'text.secondary', fontSize: 10 }}>
              Commercial
            </Typography>
            <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
          </Box>
          <Grid container spacing={2}>
            {/* Section 2: Brand + Currency (always shown) */}
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small" disabled={lockNonDraftFields}>
                <InputLabel>Brand</InputLabel>
                <Select value={transferForm.brand_id || 0} label="Brand"
                  onChange={(e) => { markTouched(); setTransferForm({ ...transferForm, brand_id: Number(e.target.value) }); }}>
                  <MenuItem value={0}><em>All brands / cross-brand</em></MenuItem>
                  {brandsList.map(b => (
                    <MenuItem key={b.id} value={Number(b.id)}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small" disabled={lockNonDraftFields}>
                <InputLabel>Currency</InputLabel>
                <Select value={transferForm.currency || 'USD'} label="Currency"
                  onChange={(e) => handleCurrencyChange(e.target.value)}>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="AUD">AUD</MenuItem>
                  <MenuItem value="SGD">SGD</MenuItem>
                  <MenuItem value="INR">INR</MenuItem>
                  <MenuItem value="CNY">CNY</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Incoterms"
                value={transferForm.incoterms || ''}
                onChange={(e) => setTransferForm({ ...transferForm, incoterms: e.target.value.toUpperCase() })}
                placeholder="e.g. FCA, DAP, FOB, EXW, DDP"
                helperText="Renders on the Commercial Invoice header." />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="overline" sx={{ fontWeight: 600, letterSpacing: 1, color: 'text.secondary', fontSize: 10 }}>
              Logistics &amp; Dates
            </Typography>
            <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
          </Box>
          <Grid container spacing={2}>
            {/* Section 3: Dates */}
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" type="date" label="Transfer Date"
                value={transferForm.transfer_date}
                disabled={linesLocked}
                onChange={(e) => setTransferForm({ ...transferForm, transfer_date: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" type="date" label="Dispatch Date"
                value={transferForm.dispatch_date}
                onChange={(e) => setTransferForm({ ...transferForm, dispatch_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="When goods actually left source"
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                InputProps={transferForm.dispatch_date ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip arrow title="Clear Dispatch Date (reverts to Draft on Save; or use Un-dispatch)">
                        <IconButton size="small" edge="end" sx={{ mr: 2 }}
                          onClick={() => setTransferForm({ ...transferForm, dispatch_date: '' })}>
                          <ClearIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                } : undefined}
                disabled={lockNonDraftFields} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" type="date" label="Expected Arrival"
                value={transferForm.expected_arrival_date}
                onChange={(e) => setTransferForm({ ...transferForm, expected_arrival_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="ETA at destination"
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                disabled={lockNonDraftFields} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" type="date" label="Completed Date"
                value={transferForm.completed_date}
                onChange={(e) => setTransferForm({ ...transferForm, completed_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Actual arrival / receipt"
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                InputProps={transferForm.completed_date ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip arrow title="Clear Completed Date (reverts to In Transit on Save; or use Un-receive)">
                        <IconButton size="small" edge="end" sx={{ mr: 2 }}
                          onClick={() => setTransferForm({ ...transferForm, completed_date: '' })}>
                          <ClearIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                } : undefined}
                disabled={lockNonDraftFields} />
            </Grid>

            {/* Section 4: Logistics */}
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Carrier"
                value={transferForm.carrier}
                onChange={(e) => setTransferForm({ ...transferForm, carrier: e.target.value })}
                placeholder="DHL / Maersk / DSV …"
                disabled={lockNonDraftFields} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Tracking #"
                value={transferForm.tracking_number}
                onChange={(e) => setTransferForm({ ...transferForm, tracking_number: e.target.value })}
                disabled={lockNonDraftFields} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="PO Number"
                value={transferForm.po_number}
                onChange={(e) => setTransferForm({ ...transferForm, po_number: e.target.value })}
                placeholder="Linked Purchase Order"
                helperText="Searchable from the Transfer list"
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                disabled={lockNonDraftFields} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Google Drive link"
                value={transferForm.drive_link}
                onChange={(e) => setTransferForm({ ...transferForm, drive_link: e.target.value })}
                placeholder="https://drive.google.com/…" />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small" disabled={lockNonDraftFields}>
                <InputLabel>Transport Mode</InputLabel>
                <Select value={transferForm.transport_mode || ''} label="Transport Mode"
                  onChange={(e) => { markTouched(); setTransferForm({ ...transferForm, transport_mode: e.target.value }); }}>
                  <MenuItem value="">— select —</MenuItem>
                  <MenuItem value="air">Air</MenuItem>
                  <MenuItem value="sea">Sea</MenuItem>
                  <MenuItem value="road">Road</MenuItem>
                  <MenuItem value="rail">Rail</MenuItem>
                  <MenuItem value="hand_carry">Hand-carry</MenuItem>
                  <MenuItem value="amazon_fba">Amazon FBA</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Shipping Method"
                value={transferForm.shipping_method}
                onChange={(e) => setTransferForm({ ...transferForm, shipping_method: e.target.value })}
                placeholder="e.g. FCL 20', LCL, Express"
                disabled={lockNonDraftFields} />
            </Grid>
          </Grid>

          {/* Section 5: Subsidiaries — optional overrides. Auto-resolved from
              warehouse_brand_subsidiary mapping on the backend when left at 0.
              Only set manually if the commercial-invoice seller/buyer entity
              must differ from the warehouse's mapped subsidiary. Inventory
              book-keeping is NOT affected by these fields — it derives from
              the warehouse mapping regardless. */}
          <Box sx={{ mt: 2.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="overline" sx={{ fontWeight: 600, letterSpacing: 1, color: 'text.secondary', fontSize: 10 }}>
              Invoicing Entities
            </Typography>
            <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small" disabled={lockNonDraftFields}>
                <InputLabel>From Subsidiary (override)</InputLabel>
                <Select value={transferForm.from_subsidiary_id || 0} label="From Subsidiary (override)"
                  onChange={(e) => { markTouched(); setTransferForm({ ...transferForm, from_subsidiary_id: Number(e.target.value) }); }}>
                  <MenuItem value={0}><em>Auto (from warehouse)</em></MenuItem>
                  {(subList || []).filter(s => s.is_active !== false).map(s => (
                    <MenuItem key={s.id} value={Number(s.id)}>
                      {s.name}{s.is_default ? ' (parent)' : ''} — {s.country} {s.currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small" disabled={lockNonDraftFields}>
                <InputLabel>To Subsidiary (override)</InputLabel>
                <Select value={transferForm.to_subsidiary_id || 0} label="To Subsidiary (override)"
                  onChange={(e) => { markTouched(); setTransferForm({ ...transferForm, to_subsidiary_id: Number(e.target.value) }); }}>
                  <MenuItem value={0}><em>Auto (from warehouse)</em></MenuItem>
                  {(subList || []).filter(s => s.is_active !== false).map(s => (
                    <MenuItem key={s.id} value={Number(s.id)}>
                      {s.name}{s.is_default ? ' (parent)' : ''} — {s.country} {s.currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>Items to Transfer</Typography>
            {/* Per-line layout mirrors Purchase Order entry:
                  thumbnail · SKU · Description · HSN · COO · Batch
                  Qty · Qty Transferred · Unit Cost · Disc% · Disc Total · Tax% · Landed/Unit · Subtotal/Line Total */}
            {groupTransferLines(transferForm.items).map((item, position) => {
              // A row may stand for several DB lines of one SKU — the allocator
              // splits a request across batches, or an imported/auto-built draft
              // carries one row per batch. `index` is the first underlying line so
              // the handlers keep their shape, `position` is the visual row number.
              const index = item._index ?? position;
              // A grouped multi-batch line is EDITABLE on an open (draft) transfer: its
              // value fields (qty, cost, disc, tax, hs, coo, description) route through
              // writeGroup, which spreads the edit across the underlying batch rows so
              // the split stays consistent (Rishabh 2026-09-16 "why are these greyed out
              // even though the Transfer is open"). Only the SKU field stays read-only on
              // a merged line — changing a SKU triggers per-row autofill (cost/batch/name)
              // that doesn't fit a summed line, so a SKU change is done by removing the
              // group (delete) and re-adding. `rowLocked` now gates ONLY that SKU control;
              // locked transfers (linesLocked) remain fully read-only as before. The Batch
              // column shows the read-only per-batch breakdown via item._split.
              const rowLocked = linesLocked || !!item._split;
              const rowKey = item.id ?? item._uid ?? index;
              const packingExpanded = packingOpenKeys.has(rowKey);
              const skuKey = (item.sku || '').toUpperCase();
              const meta = skuMetaCache[skuKey] || {};
              const thumbUrl = meta.image_url || '';
              const productName = meta.product_name || item.product_name || '';
              const q = Number(item.quantity) || 0;
              const p = Number(item.unit_cost) || 0;
              const d = Number(item.discount_percent) || 0;
              const tx = Number(item.tax_percent) || 0;
              const subtotal = q * p * (1 - d / 100);       // items net of discount, no tax, no freight
              const taxAmount = subtotal * (tx / 100);
              // Freight + customs allocated pro-rata to this line by value share.
              const landedPool = (Number(transferForm.freight_cost) || 0) + (Number(transferForm.customs_duty) || 0);
              const totalLinesValue = transferForm.items.reduce((s, it) => {
                const iq = Number(it.quantity) || 0; const ip = Number(it.unit_cost) || 0; const id_ = Number(it.discount_percent) || 0;
                return s + iq * ip * (1 - id_ / 100);
              }, 0);
              const freightShare = (landedPool > 0 && totalLinesValue > 0) ? landedPool * (subtotal / totalLinesValue) : 0;
              const lineTotal = subtotal + taxAmount + freightShare;  // items + tax + freight share = true landed line value
              const landedUnit = (q > 0 && freightShare > 0) ? p + (freightShare / q) : null;
              const validation = itemValidation[index] || {};
              const hasIssue = validation.missingSku || validation.missingQty;
              return (
                <Paper key={item.id ?? item._uid ?? index} variant="outlined" sx={{
                  p: 1.5, mt: 1.5, bgcolor: 'background.default', position: 'relative',
                  borderColor: hasIssue ? 'warning.main' : 'divider',
                }}>
                  <Box sx={{ position: 'absolute', top: -8, left: 12, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Chip
                      label={`Line ${position + 1}`}
                      size="small"
                      sx={{
                        height: 18, fontSize: 10, fontWeight: 600,
                        bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                      }}
                    />
                    {hasIssue && (
                      <Chip
                        label={validation.missingSku && validation.missingQty ? 'SKU + Qty missing'
                          : validation.missingSku ? 'SKU missing'
                          : 'Qty missing'}
                        size="small"
                        color="warning"
                        sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                      />
                    )}
                    {/* P5.2: discrepancy callout — appears when backend
                        receiveTransfer recorded received < shipped. Reason
                        (if any) shows on hover. */}
                    {Number(item.discrepancy_qty) > 0 && (
                      <Tooltip arrow title={item.discrepancy_reason ? `Reason: ${item.discrepancy_reason}` : 'No reason recorded — ask receiving warehouse.'}>
                        <Chip
                          label={`⚠ ${item.discrepancy_qty} units short`}
                          size="small"
                          color="error"
                          sx={{ height: 18, fontSize: 10, fontWeight: 600, cursor: 'help' }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    {/* Thumbnail rail */}
                    <Box sx={{ flex: '0 0 72px' }}>
                      {thumbUrl ? (
                        <Box component="img" src={thumbUrl} alt={item.sku}
                          sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'common.white' }}
                          onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <Box sx={{ width: 72, height: 72, borderRadius: 1, border: '1px dashed', borderColor: 'divider',
                                   bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 9 }}>{item.sku ? 'no img' : '—'}</Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Row 1 — SKU + Description + HSN + COO + Batch */}
                      <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} md={4}>
                          <Autocomplete
                            size="small"
                            // freeSolo + selectOnFocus allow the operator to TYPE any SKU
                            // even when the source warehouse has no current stock rows (e.g.
                            // inbound transfers from a supplier warehouse, where the
                            // destination receive is the first time the SKU lands in our
                            // stock). The dropdown still surfaces in-stock SKUs as the
                            // default suggestion list.
                            freeSolo
                            selectOnFocus
                            handleHomeEndKeys
                            options={skuSummary}
                            filterOptions={skuFilter}
                            getOptionLabel={(o) => (typeof o === 'string' ? o : o?.sku || '')}
                            isOptionEqualToValue={(a, b) => (typeof a === 'string' ? a : a?.sku) === (typeof b === 'string' ? b : b?.sku)}
                            value={item.sku || null}
                            // Uncontrolled inputValue (MUI manages the text) + a
                            // lightweight per-keystroke item.sku write so the list
                            // narrows on type — matches the working PO dropdown.
                            // Expensive autofill stays on onChange / onBlur.
                            onInputChange={(e, v) => handleSkuInputChange(index, v || '')}
                            onChange={(e, newValue) => {
                              // newValue is either an option object, a free-typed string, or null.
                              // Preserve the typed case — Lole-brand SKUs are stored
                              // lowercase in product_master, and uppercasing would break
                              // the cogs/sku-cost lookup. Server-side queries are
                              // case-insensitive anyway.
                              const sku = (typeof newValue === 'string')
                                ? newValue.trim()
                                : (newValue && newValue.sku) || '';
                              handleUpdateItem(index, 'sku', sku);
                            }}
                            onBlur={(e) => {
                              // Capture free-type values that the operator typed but never
                              // pressed Enter on (MUI Autocomplete only fires onChange on
                              // selection/Enter). Without this the typed value disappears
                              // when focus moves elsewhere.
                              const v = (e.target.value || '').trim();
                              if (v && v !== (item.sku || '')) handleUpdateItem(index, 'sku', v);
                            }}
                            disabled={rowLocked || !transferForm.from_warehouse_code}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option.sku} sx={{ display: 'flex', gap: 1, py: 0.5 }}>
                                {(option.thumbnail_url || option.image_url) ? (
                                  <Box component="img" src={option.thumbnail_url || option.image_url}
                                    sx={{ width: 36, height: 36, borderRadius: 0.5, objectFit: 'cover', border: '1px solid #ddd' }}
                                    onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                                ) : (
                                  <Box sx={{ width: 36, height: 36, borderRadius: 0.5, bgcolor: 'grey.200' }} />
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={600} noWrap sx={{ textTransform: 'uppercase', fontFamily: 'monospace' }}>{option.sku}</Typography>
                                  <Typography variant="caption" color="textSecondary" noWrap>{option.product_name || '(no name)'}</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  {/* Three states, not two. stockUnknown = no brand chosen, stock
                                      cannot be resolved. stockLoading = brand chosen, the engine is
                                      still computing — the SKU is selectable NOW and the number will
                                      fill in. Showing "out of stock" or "pick brand" while loading
                                      would both be lies. */}
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11,
                                    color: (option.stockUnknown || option.stockLoading) ? 'text.disabled' : (option.total_available > 0 ? 'success.dark' : 'error.dark') }}>
                                    {option.stockUnknown
                                      ? 'pick brand for stock'
                                      : option.stockLoading
                                        ? 'checking stock…'
                                        : (option.total_available > 0 ? `${Number(option.total_available).toLocaleString()} avail` : 'out of stock')}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            renderInput={(params) => <TextField {...params} label="SKU"
                              placeholder={
                                transferForm.from_warehouse_code
                                  ? (skuSummary.length > 0
                                      ? (skuSummary[0]?.stockUnknown
                                          ? 'Type to find any SKU — brand auto-fills'
                                          // While seeded from the catalog these are ALL the brand's
                                          // SKUs, not the in-stock ones — don't claim "in stock".
                                          : skuSummary[0]?.stockLoading
                                              ? 'Type to find a SKU — stock still loading'
                                              : `${skuSummary.length} SKU${skuSummary.length === 1 ? '' : 's'} in stock — or type any SKU`)
                                      : 'No stock here — type the SKU')
                                  : 'Source first'
                              }
                              inputProps={{ ...params.inputProps, style: { textTransform: 'uppercase', fontFamily: 'monospace' } }}
                            />}
                          />
                          {/* Per-line availability indicator. Shows the source-warehouse
                              stock for the selected SKU; turns red when qty > available
                              (Insufficient) or stock is zero (OOS). Mirrors the
                              option-dropdown badge but persists after selection — operator
                              wanted persistent visibility (2026-05-20). */}
                          {!linesLocked && item.sku && transferForm.from_warehouse_code && (() => {
                            const summary = skuSummary.find(s => s.sku === item.sku);
                            if (!summary) {
                              return (
                                <Typography variant="caption" sx={{ fontStyle: 'italic', fontSize: 10, color: 'text.disabled', display: 'block', lineHeight: 1.4, mt: 0.25 }}>
                                  Not in {transferForm.from_warehouse_code} batches
                                </Typography>
                              );
                            }
                            if (summary.stockUnknown) {
                              return (
                                <Typography variant="caption" sx={{ fontStyle: 'italic', fontSize: 10, color: 'text.disabled', display: 'block', lineHeight: 1.4, mt: 0.25 }}>
                                  Pick a brand to see stock at {transferForm.from_warehouse_code}
                                </Typography>
                              );
                            }
                            // Quantities arrive after the picker (see fetchAvailableStock).
                            // Must be checked BEFORE the OOS branch below: total_available is
                            // null while loading, which would otherwise render a false
                            // "OOS at <warehouse>" on a SKU that has plenty of stock.
                            if (summary.stockLoading) {
                              return (
                                <Typography variant="caption" sx={{ fontStyle: 'italic', fontSize: 10, color: 'text.disabled', display: 'block', lineHeight: 1.4, mt: 0.25 }}>
                                  Checking stock at {transferForm.from_warehouse_code}…
                                </Typography>
                              );
                            }
                            const avail = Number(summary.total_available || 0);
                            const qty = Number(item.quantity) || 0;
                            const insufficient = qty > avail;
                            const oos = avail <= 0;
                            return (
                              <Typography variant="caption" sx={{ fontStyle: 'italic', fontSize: 10, fontWeight: insufficient ? 600 : 400, lineHeight: 1.4, mt: 0.25, display: 'block',
                                color: oos ? 'error.main' : (insufficient ? 'warning.dark' : 'success.dark') }}>
                                {oos
                                  ? `OOS at ${transferForm.from_warehouse_code}`
                                  : `Available at ${transferForm.from_warehouse_code}: ${avail.toLocaleString()}${insufficient ? ` — short by ${(qty - avail).toLocaleString()}` : ''}`}
                              </Typography>
                            );
                          })()}
                        </Grid>
                        <Grid item xs={12} md={2.5}>
                          <TextField fullWidth size="small" label="Description"
                            value={productName}
                            disabled={linesLocked}
                            InputProps={{ readOnly: !!meta.product_name, sx: meta.product_name ? { bgcolor: 'grey.50' } : {} }}
                            onChange={(e) => (item._indices ? writeGroup(item, 'product_name', e.target.value) : handleUpdateItem(index, 'product_name', e.target.value))} />
                        </Grid>
                        <Grid item xs={6} md={1.5}>
                          <TextField fullWidth size="small" label="HSN"
                            value={item.hs_code || ''}
                            disabled={linesLocked}
                            onChange={(e) => (item._indices ? writeGroup(item, 'hs_code', e.target.value) : handleUpdateItem(index, 'hs_code', e.target.value))} />
                        </Grid>
                        <Grid item xs={6} md={1.5}>
                          <TextField fullWidth size="small" label="COO"
                            value={item.country_of_origin || ''}
                            disabled={linesLocked}
                            onChange={(e) => (item._indices ? writeGroup(item, 'country_of_origin', e.target.value) : handleUpdateItem(index, 'country_of_origin', e.target.value))}
                            placeholder="CN / IN / IT" />
                        </Grid>
                        <Grid item xs={12} md={2.5}>
                          {item._split ? (
                            // A locked line that the allocator drew from more than one
                            // batch: one row for the SKU, the split as sub-detail.
                            <Box sx={{ pt: 0.25 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
                                {item._split.length} batches
                              </Typography>
                              {item._split.map((b, bi) => (
                                <Typography key={`${b.batch_number}-${bi}`} variant="caption" sx={{ fontFamily: 'monospace', fontSize: 10, display: 'block', lineHeight: 1.4, color: 'text.secondary' }}>
                                  {b.batch_number || '—'} · {formatNumber(b.quantity)}
                                </Typography>
                              ))}
                            </Box>
                          ) : (
                          <FormControl fullWidth size="small">
                            <InputLabel>Batch</InputLabel>
                            <Select value={item.batch_number} label="Batch"
                              onChange={(e) => handleUpdateItem(index, 'batch_number', e.target.value)}
                              disabled={linesLocked || !item.sku}>
                              {linesLocked && item.batch_number && !(batchOptionsBySku[item.sku] || []).some(b => b.batch_number === item.batch_number) && (
                                <MenuItem value={item.batch_number}>{item.batch_number}</MenuItem>
                              )}
                              {(batchOptionsBySku[item.sku] || []).map(b => (
                                <MenuItem key={b.batch_number} value={b.batch_number}>
                                  {b.batch_number} ({formatNumber(b.current_quantity)} avail)
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          )}
                        </Grid>
                      </Grid>
                      {/* Row 2 — split into two horizontally-aligned blocks.
                          LEFT block stacks quantities (sub-row 2a) above pricing
                          (sub-row 2b). RIGHT block holds the totals breakdown +
                          delete. Pre-split, all 8 fields shared one Grid row of
                          12 cols → md=1.0-1.5 each → labels truncated to "Qt /
                          Qty... / Qty T... / Dis... / Disc T... / Tax..." at
                          1280px. Two sub-rows give each field ~md=3 = enough
                          width for the full label. */}
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexDirection: { xs: 'column', md: 'row' } }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Sub-row 2a — quantities (most-touched, on top) */}
                          <Grid container spacing={1.5} alignItems="flex-start">
                            <Grid item xs={4} md={4}>
                              <TextField fullWidth size="small" type="number" label="Qty" required
                                inputProps={{ min: 0 }}
                                value={item.quantity}
                                disabled={linesLocked}
                                onChange={(e) => (item._indices ? writeGroup(item, 'quantity', e.target.value) : handleUpdateItem(index, 'quantity', e.target.value))}
                                onBlur={() => item._indices && endGroupEdit(item)} />
                            </Grid>
                            <Grid item xs={4} md={4}>
                              {/* Auto-populated from Qty when Dispatch Date is set
                                  on the header; still hand-editable for partial ships. */}
                              <TextField fullWidth size="small" type="number" label="Qty Dispatched"
                                inputProps={{ min: 0 }}
                                value={item.quantity_shipped || ''}
                                onChange={(e) => (item._indices ? writeGroup(item, 'quantity_shipped', e.target.value) : handleUpdateItem(index, 'quantity_shipped', e.target.value))}
                                onBlur={() => item._indices && endGroupEdit(item)}
                                // Disabled until a Dispatch Date is set — at creation the operator
                                // only fills Qty; this auto-populates on dispatch and stays editable
                                // for partial ships (Rishabh 2026-07-21).
                                disabled={linesLocked || !transferForm.dispatch_date}
                                helperText={transferForm.dispatch_date ? 'left source' : 'set dispatch date'}
                                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }} />
                            </Grid>
                            <Grid item xs={4} md={4}>
                              {/* Auto-populated from Qty Dispatched when
                                  Completed Date is set on the header. */}
                              <TextField fullWidth size="small" type="number" label="Qty Transferred"
                                inputProps={{ min: 0 }}
                                value={item.quantity_received || ''}
                                onChange={(e) => (item._indices ? writeGroup(item, 'quantity_received', e.target.value) : handleUpdateItem(index, 'quantity_received', e.target.value))}
                                onBlur={() => item._indices && endGroupEdit(item)}
                                // Disabled until a Completed Date is set — auto-populates from
                                // Qty Dispatched on receipt, editable for short receipts.
                                // Receiving is the one legitimate edit of an In Transit transfer,
                                // so its received qty stays open there (job #424's lock keeps
                                // every other line field read-only; receive records the shortfall).
                                disabled={(linesLocked && existingStatus !== 'in_transit') || !transferForm.completed_date}
                                helperText={transferForm.completed_date ? 'received' : 'set completed date'}
                                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }} />
                            </Grid>
                          </Grid>
                          {/* Sub-row 2b — pricing */}
                          <Grid container spacing={1.5} alignItems="flex-start" sx={{ mt: 0.5 }}>
                            <Grid item xs={6} md={3.5}>
                              <TextField fullWidth size="small" type="number" label={`Unit Cost (${transferForm.currency})`}
                                inputProps={{ min: 0, step: 0.01 }}
                                // Show full precision while editing; rounded 2dp when idle.
                                value={costFocusIdx === index ? (item.unit_cost || 0) : Number(item.unit_cost || 0).toFixed(2)}
                                onFocus={() => setCostFocusIdx(index)}
                                onBlur={() => setCostFocusIdx(null)}
                                disabled={linesLocked}
                                error={!(Number(item.unit_cost) > 0) && !!factoryNoCostSkus[`${transferForm.from_warehouse_code || ''}|${String(item.sku || '').toUpperCase()}`]}
                                helperText={!(Number(item.unit_cost) > 0) && factoryNoCostSkus[`${transferForm.from_warehouse_code || ''}|${String(item.sku || '').toUpperCase()}`]
                                  ? 'Book the manufacture receipt first' : undefined}
                                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                                InputProps={{ startAdornment: <InputAdornment position="start" sx={{ fontSize: 12, fontWeight: 600 }}>{currencySymbol}</InputAdornment> }}
                                onChange={(e) => { const v = parseFloat(e.target.value) || 0; return item._indices ? writeGroup(item, 'unit_cost', v) : handleUpdateItem(index, 'unit_cost', v); }} />
                            </Grid>
                            <Grid item xs={3} md={2.5}>
                              <TextField fullWidth size="small" type="number" label="Disc %"
                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                                value={item.discount_percent || 0}
                                disabled={linesLocked}
                                onChange={(e) => { const v = parseFloat(e.target.value) || 0; return item._indices ? writeGroup(item, 'discount_percent', v) : handleUpdateItem(index, 'discount_percent', v); }} />
                            </Grid>
                            <Grid item xs={6} md={3.5}>
                              <TextField fullWidth size="small" label="Disc Total"
                                InputProps={{ readOnly: true, sx: { bgcolor: 'grey.50' } }}
                                value={`${transferForm.currency} ${(q * p * (d / 100)).toFixed(2)}`} />
                            </Grid>
                            <Grid item xs={3} md={2.5}>
                              <TextField fullWidth size="small" type="number" label="Tax %"
                                inputProps={{ min: 0, step: 0.1 }}
                                value={item.tax_percent || 0}
                                disabled={linesLocked}
                                onChange={(e) => { const v = parseFloat(e.target.value) || 0; return item._indices ? writeGroup(item, 'tax_percent', v) : handleUpdateItem(index, 'tax_percent', v); }}
                                // P4.1: tab past Tax% on the last item adds a new
                                // empty item — keyboard-driven entry flow so the
                                // operator doesn't need to mouse to "Add Item".
                                // Shift+Tab is reserved for natural back-nav.
                                onKeyDown={(e) => {
                                  if (e.key === 'Tab' && !e.shiftKey && index === transferForm.items.length - 1 && transferForm.from_warehouse_code) {
                                    e.preventDefault();
                                    handleAddItem();
                                  }
                                }}
                                helperText={index === transferForm.items.length - 1 ? 'Tab → add new line' : undefined}
                                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5, color: 'text.disabled' } }} />
                            </Grid>
                          </Grid>
                          {/* Sub-row 2c — OPTIONAL per-SKU packing details (Rishabh 2026-09-16:
                              cartons/weights per SKU, not per whole transfer). Collapsed by
                              default so the line stays clean; opens on click. Grouped lines
                              route through writeGroup (applies to the SKU's batch rows). */}
                          <Box sx={{ mt: 1 }}>
                            <Box onClick={() => togglePacking(rowKey)}
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', userSelect: 'none' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                {packingExpanded ? '▾' : '▸'} Packing details
                              </Typography>
                              {!packingExpanded && (Number(item.cartons) > 0 || Number(item.per_case) > 0 || Number(item.net_weight_kg) > 0 || Number(item.gross_weight_kg) > 0) && (
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace', fontSize: 10 }}>
                                  {Number(item.cartons) > 0 ? `${formatNumber(item.cartons)} ctn` : ''}
                                  {Number(item.per_case) > 0 ? ` · ${formatNumber(item.per_case)}/ctn` : ''}
                                  {Number(item.net_weight_kg) > 0 ? ` · net ${item.net_weight_kg}kg` : ''}
                                  {Number(item.gross_weight_kg) > 0 ? ` · gr ${item.gross_weight_kg}kg` : ''}
                                </Typography>
                              )}
                            </Box>
                            {packingExpanded && (
                              <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                                <Grid item xs={6} md={3}>
                                  <TextField fullWidth size="small" type="number" label="Cartons"
                                    inputProps={{ min: 0 }}
                                    value={item.cartons ?? ''}
                                    disabled={linesLocked}
                                    onChange={(e) => (item._indices ? writeGroup(item, 'cartons', e.target.value) : handleUpdateItem(index, 'cartons', e.target.value))} />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                  <TextField fullWidth size="small" type="number" label="Units / carton"
                                    inputProps={{ min: 0 }}
                                    value={item.per_case ?? ''}
                                    disabled={linesLocked}
                                    onChange={(e) => (item._indices ? writeGroup(item, 'per_case', e.target.value) : handleUpdateItem(index, 'per_case', e.target.value))} />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                  <TextField fullWidth size="small" type="number" label="Net wt (kg)"
                                    inputProps={{ min: 0, step: 0.01 }}
                                    value={item.net_weight_kg ?? ''}
                                    disabled={linesLocked}
                                    onChange={(e) => (item._indices ? writeGroup(item, 'net_weight_kg', e.target.value) : handleUpdateItem(index, 'net_weight_kg', e.target.value))} />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                  <TextField fullWidth size="small" type="number" label="Gross wt (kg)"
                                    inputProps={{ min: 0, step: 0.01 }}
                                    value={item.gross_weight_kg ?? ''}
                                    disabled={linesLocked}
                                    onChange={(e) => (item._indices ? writeGroup(item, 'gross_weight_kg', e.target.value) : handleUpdateItem(index, 'gross_weight_kg', e.target.value))} />
                                </Grid>
                              </Grid>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0, alignSelf: { md: 'stretch' } }}>
                          <Box sx={{
                            display: 'flex', flexDirection: 'column', height: '100%',
                            justifyContent: 'space-between',
                            p: 1, borderRadius: 1,
                            bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                          }}>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1, textAlign: 'right', color: 'text.secondary' }}>
                                  {transferForm.currency} {subtotal.toFixed(2)}
                                </Typography>
                              </Box>
                              {taxAmount > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                  <Typography variant="caption" color="text.secondary">+ Tax</Typography>
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1, textAlign: 'right', color: 'text.secondary' }}>
                                    {transferForm.currency} {taxAmount.toFixed(2)}
                                  </Typography>
                                </Box>
                              )}
                              {freightShare > 0 && (
                                <Tooltip title={`Pro-rata freight+customs allocation: ${(subtotal/totalLinesValue*100).toFixed(1)}% of ${transferForm.currency} ${landedPool.toFixed(2)}`} arrow>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', cursor: 'help' }}>
                                    <Typography variant="caption" color="info.main">+ Freight share</Typography>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1, textAlign: 'right', color: 'info.main', borderBottom: '1px dotted', borderColor: 'info.light' }}>
                                      {transferForm.currency} {freightShare.toFixed(2)}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              )}
                              {landedUnit !== null && (
                                <Tooltip title={`Unit cost + (freight + customs share / qty)`} arrow>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', cursor: 'help' }}>
                                    <Typography variant="caption" color="info.main" sx={{ fontSize: 10 }}>Landed/Unit</Typography>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1, textAlign: 'right', color: 'info.main', fontSize: 10 }}>
                                      {transferForm.currency} {landedUnit.toFixed(2)}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', borderTop: '1px solid', borderColor: 'divider', mt: 0.5, pt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Line Total</Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', flex: 1, textAlign: 'right' }}>
                                {transferForm.currency} {lineTotal.toFixed(2)}
                              </Typography>
                              <IconButton size="small" color="error"
                                onClick={() => (item._indices ? handleRemoveItemGroup(item._indices) : handleRemoveItem(index))}
                                // Keep the single-line floor: don't allow a delete (single or
                                // whole-group) that would remove every remaining row.
                                disabled={linesLocked || transferForm.items.length === 1
                                  || (!!item._indices && item._indices.length >= transferForm.items.length)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
            {/* P5.3: items footer — quick aggregate strip directly under the
                items list. Mirrors the action bar's "1 line · N units" but
                adds qty totals across all three lifecycle states (requested
                / dispatched / received) + per-discrepancy unit count. */}
            {transferForm.items.length > 0 && (
              <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1, border: '1px dashed', borderColor: 'divider', display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: 12, color: 'text.secondary' }}>
                <span><strong>{transferForm.items.length}</strong> line{transferForm.items.length === 1 ? '' : 's'}</span>
                <span>·</span>
                <span>
                  Qty requested: <strong>{transferForm.items.reduce((s, it) => s + (Number(it.quantity) || 0), 0).toLocaleString()}</strong>
                </span>
                {transferForm.items.some(it => Number(it.quantity_shipped) > 0) && (
                  <span>
                    Dispatched: <strong>{transferForm.items.reduce((s, it) => s + (Number(it.quantity_shipped) || 0), 0).toLocaleString()}</strong>
                  </span>
                )}
                {transferForm.items.some(it => Number(it.quantity_received) > 0) && (
                  <span>
                    Transferred: <strong>{transferForm.items.reduce((s, it) => s + (Number(it.quantity_received) || 0), 0).toLocaleString()}</strong>
                  </span>
                )}
                {transferForm.items.some(it => Number(it.discrepancy_qty) > 0) && (
                  <span style={{ color: '#d32f2f' }}>
                    ⚠ {transferForm.items.reduce((s, it) => s + (Number(it.discrepancy_qty) || 0), 0).toLocaleString()} short
                  </span>
                )}
              </Box>
            )}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              sx={{ alignSelf: 'flex-start', mt: 1.5 }}
              disabled={linesLocked || !transferForm.from_warehouse_code}
            >
              Add Item
            </Button>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          {/* Shipping & landed cost — visible for ALL transfers (was previously
              gated to cross-border or supplier-source only). Freight applies to
              any inter-warehouse move; customs is optional and naturally zero
              for domestic transfers but the field stays available. */}
          {(() => {
            const fromWh = warehouses.find(w => w.code === transferForm.from_warehouse_code);
            const toWh   = warehouses.find(w => w.code === transferForm.to_warehouse_code);
            const crossBorder = fromWh && toWh && fromWh.country && toWh.country && fromWh.country !== toWh.country;
            const itemsValue = (transferForm.items || []).reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_cost) || 0), 0);
            const totalLanded = (parseFloat(transferForm.freight_cost) || 0) + (parseFloat(transferForm.customs_duty) || 0);
            const totalQty = (transferForm.items || []).reduce((s, it) => s + (parseFloat(it.quantity) || 0), 0);
            const perUnitLanded = totalQty > 0 ? (totalLanded / totalQty).toFixed(4) : '0.0000';
            const heading = crossBorder
              ? `Shipping & Landed Cost — cross-border (${fromWh.country} → ${toWh.country})`
              : 'Shipping & Landed Cost';
            return (
              <Paper sx={{ p: 2, bgcolor: crossBorder ? 'warning.light' : 'grey.50', mt: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {heading}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={4}>
                    <TextField label={`Freight Cost (${transferForm.currency || 'USD'})`} type="number" size="small" fullWidth
                      value={transferForm.freight_cost}
                      onChange={(e) => setTransferForm({...transferForm, freight_cost: parseFloat(e.target.value) || 0})}
                      disabled={lockNonDraftFields}
                      helperText="Carrier / shipping line charges"
                      FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                    />
                    {/* Freight invoiced in another currency → convert into the field above */}
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
                      <TextField type="number" size="small" placeholder="Amt" sx={{ width: 70 }}
                        value={fxConvert.freight.amount} disabled={lockNonDraftFields}
                        onChange={(e) => setFxConvert(p => ({ ...p, freight: { ...p.freight, amount: e.target.value } }))} />
                      <FormControl size="small" sx={{ width: 80 }} disabled={lockNonDraftFields}>
                        <Select displayEmpty value={fxConvert.freight.currency}
                          onChange={(e) => setFxConvert(p => ({ ...p, freight: { ...p.freight, currency: e.target.value } }))}>
                          <MenuItem value=""><em>ccy</em></MenuItem>
                          {['USD','GBP','EUR','AUD','SGD','INR','CNY'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1 }}
                        disabled={lockNonDraftFields || fxConvert.freight.converting || !fxConvert.freight.amount || !fxConvert.freight.currency}
                        onClick={() => applyForeignLandedCost('freight')}>
                        {fxConvert.freight.converting ? '…' : `→ ${transferForm.currency || 'USD'}`}
                      </Button>
                    </Box>
                    {fxConvert.freight.note && (
                      <Typography variant="caption" color={fxConvert.freight.note.startsWith('FX rate unavailable') ? 'error' : 'textSecondary'} sx={{ fontSize: 10, display: 'block', mt: 0.25 }}>{fxConvert.freight.note}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <TextField label={`Customs Duty (${transferForm.currency || 'USD'})`} type="number" size="small" fullWidth
                      value={transferForm.customs_duty}
                      onChange={(e) => setTransferForm({...transferForm, customs_duty: parseFloat(e.target.value) || 0})}
                      disabled={lockNonDraftFields}
                      helperText={crossBorder ? 'Duty + clearance fees' : 'Optional — usually 0 for domestic'}
                      FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                    />
                    {/* Customs invoiced in another currency → convert into the field above */}
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
                      <TextField type="number" size="small" placeholder="Amt" sx={{ width: 70 }}
                        value={fxConvert.customs.amount} disabled={lockNonDraftFields}
                        onChange={(e) => setFxConvert(p => ({ ...p, customs: { ...p.customs, amount: e.target.value } }))} />
                      <FormControl size="small" sx={{ width: 80 }} disabled={lockNonDraftFields}>
                        <Select displayEmpty value={fxConvert.customs.currency}
                          onChange={(e) => setFxConvert(p => ({ ...p, customs: { ...p.customs, currency: e.target.value } }))}>
                          <MenuItem value=""><em>ccy</em></MenuItem>
                          {['USD','GBP','EUR','AUD','SGD','INR','CNY'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1 }}
                        disabled={lockNonDraftFields || fxConvert.customs.converting || !fxConvert.customs.amount || !fxConvert.customs.currency}
                        onClick={() => applyForeignLandedCost('customs')}>
                        {fxConvert.customs.converting ? '…' : `→ ${transferForm.currency || 'USD'}`}
                      </Button>
                    </Box>
                    {fxConvert.customs.note && (
                      <Typography variant="caption" color={fxConvert.customs.note.startsWith('FX rate unavailable') ? 'error' : 'textSecondary'} sx={{ fontSize: 10, display: 'block', mt: 0.25 }}>{fxConvert.customs.note}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl size="small" fullWidth disabled={lockNonDraftFields}>
                      <InputLabel>Allocation Method</InputLabel>
                      <Select value={transferForm.landed_cost_method} label="Allocation Method"
                        onChange={(e) => { markTouched(); setTransferForm({...transferForm, landed_cost_method: e.target.value}); }}>
                        <MenuItem value="by_value">By Value</MenuItem>
                        <MenuItem value="by_qty">By Quantity</MenuItem>
                        <MenuItem value="by_weight">By Weight</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Total landed cost: {totalLanded.toFixed(2)} · Items value: {itemsValue.toFixed(2)} · Total qty: {totalQty} · Per-unit landed: {perUnitLanded}
                </Typography>
              </Paper>
            );
          })()}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Internal Notes"
                value={transferForm.notes}
                onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                multiline
                rows={2}
                fullWidth
                helperText="Ops-only — never leaves our system"
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                disabled={lockNonDraftFields}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Customer Notes (on docs)"
                value={transferForm.customer_notes}
                onChange={(e) => setTransferForm({ ...transferForm, customer_notes: e.target.value })}
                multiline
                rows={2}
                fullWidth
                helperText="Shows on commercial invoice / packing list to the receiving party"
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                disabled={lockNonDraftFields}
              />
            </Grid>
          </Grid>
          <TextField
            label="Description of Goods"
            value={transferForm.description_of_goods || ''}
            onChange={(e) => setTransferForm({ ...transferForm, description_of_goods: e.target.value })}
            multiline
            rows={2}
            fullWidth
            helperText="Commercial-invoice / customs description (required for cross-border)"
            disabled={lockNonDraftFields}
          />

          {/* Packing details moved to the per-SKU line (Rishabh 2026-09-16: cartons and
              weights are captured per SKU, not once for the whole transfer). The whole-
              transfer Cartons / Units-per-case / Gross / Net fields were removed here; the
              packing list totals now add up the per-line values. */}
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Packing List</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Cartons, units per carton and net / gross weight are now entered <strong>per SKU</strong> —
            open the “Packing details” toggle on each item line. The packing list totals add up the lines.
          </Typography>

          {/* Pallet details — one set for the whole transfer (Rishabh 2026-09-16). Prints on
              the packing list below the shipment totals. */}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Pallet Details</Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6} md={3}>
              <TextField
                label="No. of Pallets"
                type="number"
                size="small"
                fullWidth
                inputProps={{ min: 0, step: 1 }}
                value={transferForm.pallet_count}
                onChange={(e) => setTransferForm({ ...transferForm, pallet_count: e.target.value })}
                disabled={lockNonDraftFields}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Dimensions"
                size="small"
                fullWidth
                value={transferForm.pallet_dimensions}
                onChange={(e) => setTransferForm({ ...transferForm, pallet_dimensions: e.target.value })}
                placeholder="e.g. 120 x 100 x 150 cm"
                helperText="Free text — L x W x H, per pallet or overall"
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                disabled={lockNonDraftFields}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Total Pallet Weight (kg)"
                type="number"
                size="small"
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                value={transferForm.pallet_weight_kg}
                onChange={(e) => setTransferForm({ ...transferForm, pallet_weight_kg: e.target.value })}
                disabled={lockNonDraftFields}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* P5.4: PDF preview modal — iframes the generated invoice/packing-list
          PDF so the operator can verify content before downloading. Replaces
          the previous "click → silent download" flow. */}
      <Dialog open={pdfPreview.open} onClose={closePdfPreview} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            {pdfPreview.kind === 'invoice' ? 'Commercial Invoice — Preview' : 'Packing List — Preview'}
            {pdfPreview.filename && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: 11 }}>
                {pdfPreview.filename}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '70vh', bgcolor: 'background.default' }}>
          {pdfPreview.loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">Generating PDF…</Typography>
            </Box>
          ) : pdfPreview.url ? (
            <Box component="iframe" src={pdfPreview.url} sx={{ width: '100%', height: '100%', border: 0 }} title="PDF preview" />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePdfPreview}>Close</Button>
          <Button variant="contained" startIcon={<DescriptionIcon />} disabled={!pdfPreview.url} onClick={downloadPdfPreview}>
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Currency-change confirm dialog */}
      <Dialog
        open={currencyConvertDialog.open}
        onClose={() => applyCurrencyConvert('cancel')}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Currency: {currencyConvertDialog.oldCurrency} → {currencyConvertDialog.newCurrency}
        </DialogTitle>
        <DialogContent>
          {currencyConvertDialog.loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Fetching exchange rate…</Typography>
            </Box>
          ) : currencyConvertDialog.error ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {currencyConvertDialog.error} — you can still relabel the currency without converting values.
            </Alert>
          ) : currencyConvertDialog.rate ? (
            <Typography variant="body2">
              Convert all entered values at{' '}
              <strong>
                1 {currencyConvertDialog.oldCurrency} = {Number(currencyConvertDialog.rate).toFixed(4)} {currencyConvertDialog.newCurrency}
              </strong>?
              <Box component="span" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontSize: 12 }}>
                This multiplies <em>unit costs</em>, <em>freight cost</em>, and <em>customs duty</em> by the rate.
                Choose <strong>No, just relabel</strong> if the values are already in {currencyConvertDialog.newCurrency} and you just clicked the wrong currency first.
              </Box>
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => applyCurrencyConvert('cancel')}>Cancel</Button>
          <Button onClick={() => applyCurrencyConvert('relabel')}>No, just relabel</Button>
          <Button
            variant="contained"
            disabled={currencyConvertDialog.loading || !currencyConvertDialog.rate}
            onClick={() => applyCurrencyConvert('convert')}
          >
            Yes, convert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pre-ship stock-shortage confirm (Rishabh 2026-06-19) */}
      <Dialog open={shortageDialog.open} onClose={() => setShortageDialog(prev => ({ ...prev, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'warning.dark' }}>Confirm before shipping — issues found</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {shortageDialog.lines.length} SKU{shortageDialog.lines.length === 1 ? '' : 's'} on this transfer {shortageDialog.lines.length === 1 ? 'has' : 'have'} an issue — short on available stock and/or missing a cost — at <b>{transferForm.from_warehouse_code}</b>. Review, then decide whether to ship anyway.
          </Typography>
          {shortageDialog.lines.map((l, i) => (
            <Box key={i} sx={{ p: 1.25, mb: 1, border: '1px solid', borderColor: l.isShort ? 'error.light' : 'warning.light', borderRadius: 1, bgcolor: l.isShort ? 'rgba(211,47,47,0.04)' : 'rgba(237,108,2,0.05)' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{l.sku}</Typography>
              {l.isShort && (
                <>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                    Need <b>{Number(l.requested).toLocaleString()}</b> · Available at {transferForm.from_warehouse_code} <b style={{ color: '#c62828' }}>{Number(l.available).toLocaleString()}</b> · Short by <b style={{ color: '#c62828' }}>{Number(l.short).toLocaleString()}</b>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Incoming (open POs / inbound transfers): {l.incomingLoading ? <CircularProgress size={10} sx={{ ml: 0.5 }} /> : (l.incoming == null ? '—' : Number(l.incoming).toLocaleString())}
                  </Typography>
                  {!l.incomingLoading && Array.isArray(l.incomingItems) && l.incomingItems.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10, mt: 0.25 }}>
                      {l.incomingItems.map((x) => {
                        const q = Number(x.quantity ?? x.incoming_quantity ?? x.qty ?? 0) || 0;
                        const src = x.source_type || x.source || x.type || x.reference || 'incoming';
                        const eta = x.eta || x.expected_date || x.expected_arrival_date || x.receipt_date || '';
                        return `${src} ${q.toLocaleString()}${eta ? ` (ETA ${String(eta).slice(0, 10)})` : ''}`;
                      }).join(' · ')}
                    </Typography>
                  )}
                </>
              )}
              {l.missingCost && (
                <Typography variant="caption" sx={{ display: 'block', mt: l.isShort ? 0.5 : 0.25, color: 'warning.dark', fontWeight: 600 }}>
                  ⚠ Missing cost — will ship with blank / zero unit cost.
                </Typography>
              )}
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary">
            "Available" is the on-hand count at the source warehouse that can actually ship now. Shipping anyway proceeds with whatever stock / cost exists.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShortageDialog(prev => ({ ...prev, open: false }))}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => {
            const mode = shortageDialog.pendingMode;
            setShortageDialog(prev => ({ ...prev, open: false }));
            handleSave(mode, true);
          }}>Ship anyway</Button>
        </DialogActions>
      </Dialog>

      {/* Sticky action bar */}
      <Box sx={{
        position: 'fixed', bottom: 0, left: { xs: 0, md: 240 }, right: 0,
        bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
        px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        zIndex: 10, boxShadow: '0 -4px 16px -8px rgba(0,0,0,0.10)',
      }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {transferForm.items.length} line{transferForm.items.length === 1 ? '' : 's'} ·
            {' '}{transferForm.items.reduce((s, it) => s + (Number(it.quantity) || 0), 0)} units
          </Typography>
          {(() => {
            const itemsWithTax = transferForm.items.reduce((s, it) => {
              const q = Number(it.quantity) || 0;
              const p = Number(it.unit_cost) || 0;
              const d = Number(it.discount_percent) || 0;
              const tx = Number(it.tax_percent) || 0;
              return s + q * p * (1 - d/100) * (1 + tx/100);
            }, 0);
            const freightPlusCustoms = (Number(transferForm.freight_cost) || 0) + (Number(transferForm.customs_duty) || 0);
            const grandTotal = itemsWithTax + freightPlusCustoms;
            return (
              <>
                {freightPlusCustoms > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10, lineHeight: 1.2 }}>
                    Items+tax: {transferForm.currency} {itemsWithTax.toFixed(2)} · Freight/customs: {transferForm.currency} {freightPlusCustoms.toFixed(2)}
                  </Typography>
                )}
                <Typography variant="h6" fontWeight={700}>
                  {transferForm.currency} {grandTotal.toFixed(2)}
                </Typography>
              </>
            );
          })()}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {invalidItemCount > 0 && (
            <Tooltip arrow title="Each line needs a SKU and a positive quantity. Highlighted line(s) above show what's missing.">
              <Chip
                label={`Fix ${invalidItemCount} item${invalidItemCount === 1 ? '' : 's'}`}
                size="small"
                color="warning"
                sx={{ fontWeight: 600 }}
              />
            </Tooltip>
          )}
          {/* No footer "Cancel" — it duplicated the Back arrow, which is now
              the single guarded "leave" control. Matches PurchaseOrderEntry. */}
          {isEdit && (
            <Box sx={{ width: '1px', height: 24, bgcolor: 'divider', mx: 0.5 }} />
          )}
          {/* Forwarder document shortcuts — only available once the transfer
              has been saved (need an id to generate from). Same endpoints as
              the detail page's Documents card. CI / Packing List share an id
              server-side (generate is idempotent on the transfer). */}
          {isEdit && (
            <>
              <Button variant="outlined" startIcon={<DescriptionIcon />} disabled={saving}
                onClick={() => openPdfPreview('invoice')}>
                Commercial Invoice
              </Button>
              <Button variant="outlined" startIcon={<DescriptionIcon />} disabled={saving}
                onClick={() => openPdfPreview('packing-list')}>
                Packing List
              </Button>
            </>
          )}
          {isEdit && (
            <Box sx={{ width: '1px', height: 24, bgcolor: 'divider', mx: 0.5 }} />
          )}
          {/* ONE primary save; the stay-on-page save is secondary and now says
              so in its label rather than only in a tooltip. Same pattern as
              PurchaseOrderEntry so both editors behave identically. Tooltips
              keep the keyboard shortcuts, which is genuine tooltip material. */}
          {isEdit ? (
            <>
              <Tooltip arrow title={<span><strong>⌘ Enter</strong></span>}>
                <Button variant="text" onClick={() => handleSave('stay')} disabled={saving}>
                  {saving ? 'Saving…' : 'Save & keep editing'}
                </Button>
              </Tooltip>
              <Tooltip arrow title={<span><strong>⌘ Shift Enter</strong></span>}>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={() => handleSave('back')} disabled={saving}>
                  {saving ? 'Saving…' : 'Save & Close'}
                </Button>
              </Tooltip>
            </>
          ) : (
            <Tooltip arrow title={<span>Create transfer<br /><strong>⌘ Enter</strong></span>}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={() => handleSave('back')} disabled={saving}>
                {saving ? 'Saving…' : 'Create Transfer'}
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}
